/**
 * AdminCloudServers — Professional server management dashboard
 * Inspired by DigitalOcean + Hetzner Cloud Console + Kubernetes Dashboard
 * Route: /admin/servers
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'preact/hooks';
import type { JSX } from 'preact';
import { api } from '../api/client';
import { showToast } from '../components/Toast';
import { T } from '../styles/tokens';
import { Button, ListSkeleton, SkeletonBar, Textarea } from '../components/ui';

// ══════════════════════════════════════════════════════════════════════════
// SVG ICON COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

function IconCheck({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconX({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
}
function IconPulse({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
}
function IconSkip({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>;
}
function IconCircle({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2"><circle cx="12" cy="12" r="10"/></svg>;
}
function IconServer({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>;
}
function IconActivity({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
}
function IconRefresh({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
}
function IconCopy({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
}
function IconExpand({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>;
}
function IconChevronDown({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
}
function IconChevronUp({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>;
}
function IconClock({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
function IconLogs({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
}
function IconRetry({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>;
}

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface CloudServer {
  id: string;
  name: string;
  hostname: string | null;
  ip_address: string;
  ssh_port: number;
  ssh_user: string;
  vault_credential_id: string | null;
  region: string | null;
  provider: string | null;
  status: string;
  docker_version: string | null;
  docker_compose_version: string | null;
  os_info: string | null;
  cpu_cores: number | null;
  ram_mb: number | null;
  disk_gb: number | null;
  max_containers: number;
  active_containers: number;
  plan_tiers: string | null;
  last_health_check: string | null;
  health_status: string;
  health_details: string | null;
  tags: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ServerEvent {
  id: string;
  server_id: string;
  event_type: string;
  event_data: string | null;
  created_at: string;
}

interface ProvisioningJob {
  id: string;
  server_id: string;
  job_type: string;
  status: string;
  payload: string | null;
  result: string | null;
  error: string | null;
  progress: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface JobProgress {
  currentStep?: string;
  stepIndex?: number;
  totalSteps?: number;
  percentage?: number;
  logs?: string[];
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface HealthCheck {
  name: string;
  result: string;
  message: string;
}

// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG: Record<string, { color: string; label: string; icon: string }> = {
  pending:      { color: T.warning, label: 'Pendente',    icon: '' },
  validated:    { color: T.textMuted, label: 'Validado',    icon: '' },
  installing:   { color: T.warning, label: 'Instalando',  icon: '' },
  ready:        { color: T.success, label: 'Pronto',      icon: '●' },
  maintenance:  { color: T.primary, label: 'Manutenção',  icon: '' },
  error:        { color: T.danger, label: 'Erro',        icon: '' },
};

const HEALTH_COLORS: Record<string, string> = {
  healthy: T.success, degraded: T.warning, unhealthy: T.danger, unknown: T.textMuted,
  pass: T.success, warn: T.warning, fail: T.danger,
};

const PLAN_TIERS = ['free', 'starter', 'pro', 'business', 'enterprise'] as const;

const PLAN_COLORS: Record<string, string> = {
  free: T.textMuted, starter: T.primary, pro: T.purple, business: T.warning, enterprise: T.danger,
};

const EVENT_ICONS: Record<string, string> = {
  status_change: '', health_check: '', validate: '', setup: '',
  maintenance: '', activate: '', error: '', provision: '',
  created: '🆕', deleted: '',
};

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Agora';
  if (mins < 60) return `${mins}min atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

function parsePlanTiers(raw: string | null): string[] {
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

function parseJSON<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

function loadPercent(server: CloudServer): number {
  return server.max_containers > 0
    ? Math.round((server.active_containers / server.max_containers) * 100)
    : 0;
}

function loadColor(pct: number): string {
  if (pct >= 90) return T.danger;
  if (pct >= 70) return T.warning;
  return T.accent;
}

function copyToClipboard(text: string): void {
  navigator.clipboard.writeText(text).then(
    () => showToast('IP copiado!', 'success'),
    () => showToast('Erro ao copiar', 'error'),
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SMALL COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

function StatusLED({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { color: T.textMuted, label: status, icon: '?' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 10, height: 10, borderRadius: '50%', background: cfg.color,
        boxShadow: `0 0 6px ${cfg.color}88`,
        display: 'inline-block',
        animation: status === 'installing' ? 'pulse 1.5s infinite' : undefined,
      }} />
      <span style={{
        fontSize: 12, fontWeight: 600, color: cfg.color,
        padding: '2px 8px', borderRadius: 6, background: `${cfg.color}15`,
      }}>
        {cfg.label}
      </span>
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const color = PLAN_COLORS[plan] ?? T.textMuted;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: `${color}20`, color, textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    }}>
      {plan}
    </span>
  );
}

function ProgressBar({ value, max, height = 8 }: { value: number; max: number; height?: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const color = loadColor(pct);
  return (
    <div style={{ height, borderRadius: height / 2, background: T.borderHover, overflow: 'hidden', width: '100%' }}>
      <div style={{
        height: '100%', borderRadius: height / 2, transition: 'width 0.5s ease',
        width: `${pct}%`,
        background: pct < 70 ? `linear-gradient(90deg, #7c5cfc, ${T.primary})` : color,
      }} />
    </div>
  );
}

function KPICard({ icon, label, value, sub, color }: { icon: string; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #2F2F2F 0%, #363636 100%)',
      border: `1px solid ${T.bgHover}`, borderRadius: 12, padding: '16px 20px',
      flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12,
      padding: 20,
    }}>
      <SkeletonBar width="60%" height={16} style={{ marginBottom: 12 }} />
      <SkeletonBar width="80%" height={12} style={{ marginBottom: 8 }} />
      <SkeletonBar width="100%" height={8} style={{ marginBottom: 12 }} />
      <SkeletonBar width="40%" height={12} />
    </div>
  );
}

function EmptyState({ icon, title, desc, action, onAction }: {
  icon: string; title: string; desc: string; action?: string; onAction?: () => void;
}) {
  return (
    <div style={{
      textAlign: 'center', padding: '60px 20px', background: `${T.border}`,
      borderRadius: 16, border: '1px dashed #374151',
    }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>{desc}</div>
      {action && onAction && (
        <Button onClick={onAction}>
          {action}
        </Button>
      )}
    </div>
  );
}

function ConfirmDialog({ title, message, onConfirm, onCancel }: {
  title: string; message: string; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: T.bgOverlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: T.borderHover, borderRadius: 16, padding: 24, maxWidth: 400, width: '90%' }}>
        <h3 style={{ margin: '0 0 8px', color: T.text, fontSize: 16 }}>{title}</h3>
        <p style={{ color: T.textSecondary, fontSize: 13, margin: '0 0 20px' }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
          <Button variant="ghost" onClick={onConfirm}>Confirmar</Button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ADD SERVER MODAL (Enhanced)
// ══════════════════════════════════════════════════════════════════════════

function AddServerModalEnhanced({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [sshPort, setSshPort] = useState(22);
  const [sshUser, setSshUser] = useState('root');
  const [region, setRegion] = useState('');
  const [provider, setProvider] = useState('');
  const [cpuCores, setCpuCores] = useState<number | ''>('');
  const [ramMb, setRamMb] = useState<number | ''>('');
  const [diskGb, setDiskGb] = useState<number | ''>('');
  const [maxContainers, setMaxContainers] = useState(10);
  const [planTiers, setPlanTiers] = useState<string[]>([...PLAN_TIERS]);
  const [notes, setNotes] = useState('');
  const [autoSetup, setAutoSetup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Nome é obrigatório';
    if (!ipAddress.trim()) errs.ip = 'IP é obrigatório';
    else if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ipAddress.trim()))
      errs.ip = 'IP inválido';
    if (sshPort < 1 || sshPort > 65535) errs.port = 'Porta inválida';
    if (maxContainers < 1) errs.max = 'Mínimo 1 container';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(), ip_address: ipAddress.trim(), ssh_port: sshPort,
        ssh_user: sshUser, max_containers: maxContainers, plan_tiers: planTiers,
      };
      if (hostname) body.hostname = hostname;
      if (region) body.region = region;
      if (provider) body.provider = provider;
      if (cpuCores) body.cpu_cores = cpuCores;
      if (ramMb) body.ram_mb = ramMb;
      if (diskGb) body.disk_gb = diskGb;
      if (notes) body.notes = notes;

      const result = await api.post<{ server: { id: string } }>('/cloud/admin/servers', body);
      if (autoSetup && result.server?.id) {
        await api.post(`/cloud/admin/servers/${result.server.id}/validate`, {});
      }
      showToast('Servidor adicionado com sucesso!', 'success');
      onCreated();
    } catch {
      showToast('Erro ao adicionar servidor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const togglePlan = (plan: string) => {
    setPlanTiers(prev => prev.includes(plan) ? prev.filter(p => p !== plan) : [...prev, plan]);
  };

  const inputStyle = (hasError?: boolean) => ({
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1px solid ${hasError ? T.danger : T.textDisabled}`, background: T.bg, color: T.text,
    fontSize: 13, boxSizing: 'border-box' as const, transition: 'border-color 0.2s',
    outline: 'none',
  });

  const labelStyle = { fontSize: 12, color: T.textSecondary, marginBottom: 4, display: 'block' as const, fontWeight: 500 as const };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: T.bgOverlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: `${T.border}`, borderRadius: 20, padding: 28, width: '100%',
        maxWidth: 560, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.bgHover}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h3 style={{ margin: 0, color: T.text, fontSize: 18, fontWeight: 700 }}>Adicionar Servidor</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: T.textMuted }}>Configure um novo servidor para hospedar containers</p>
          </div>
          <Button variant="ghost" onClick={onClose}></Button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Connection */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, marginBottom: 10, textTransform: 'uppercase' as const, letterSpacing: '1px' }}>Conexão</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Nome *</label>
                <input style={inputStyle(!!errors.name)} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} placeholder="srv-pool-01" />
                {errors.name && <div style={{ fontSize: 11, color: T.danger, marginTop: 2 }}>{errors.name}</div>}
              </div>
              <div>
                <label style={labelStyle}>IP Address *</label>
                <input style={inputStyle(!!errors.ip)} value={ipAddress} onInput={(e) => setIpAddress((e.target as HTMLInputElement).value)} placeholder="72.61.60.201" />
                {errors.ip && <div style={{ fontSize: 11, color: T.danger, marginTop: 2 }}>{errors.ip}</div>}
              </div>
              <div>
                <label style={labelStyle}>Hostname</label>
                <input style={inputStyle()} value={hostname} onInput={(e) => setHostname((e.target as HTMLInputElement).value)} placeholder="srv1.example.com" />
              </div>
              <div>
                <label style={labelStyle}>Provedor</label>
                <input style={inputStyle()} value={provider} onInput={(e) => setProvider((e.target as HTMLInputElement).value)} placeholder="k8s" />
              </div>
              <div>
                <label style={labelStyle}>Região</label>
                <input style={inputStyle()} value={region} onInput={(e) => setRegion((e.target as HTMLInputElement).value)} placeholder="us-east" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={labelStyle}>Porta SSH</label>
                  <input style={inputStyle(!!errors.port)} type="number" value={sshPort} onInput={(e) => setSshPort(parseInt((e.target as HTMLInputElement).value) || 22)} />
                </div>
                <div>
                  <label style={labelStyle}>User SSH</label>
                  <input style={inputStyle()} value={sshUser} onInput={(e) => setSshUser((e.target as HTMLInputElement).value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Hardware */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, marginBottom: 10, textTransform: 'uppercase' as const, letterSpacing: '1px' }}>Hardware</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>CPU Cores</label>
                <input style={inputStyle()} type="number" value={cpuCores} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setCpuCores(v ? parseInt(v) : ''); }} />
              </div>
              <div>
                <label style={labelStyle}>RAM (MB)</label>
                <input style={inputStyle()} type="number" value={ramMb} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setRamMb(v ? parseInt(v) : ''); }} />
              </div>
              <div>
                <label style={labelStyle}>Disco (GB)</label>
                <input style={inputStyle()} type="number" value={diskGb} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setDiskGb(v ? parseInt(v) : ''); }} />
              </div>
              <div>
                <label style={labelStyle}>Máx. Containers</label>
                <input style={inputStyle(!!errors.max)} type="number" value={maxContainers} onInput={(e) => setMaxContainers(parseInt((e.target as HTMLInputElement).value) || 10)} />
              </div>
            </div>
          </div>

          {/* Plans */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, marginBottom: 10, textTransform: 'uppercase' as const, letterSpacing: '1px' }}>Planos Suportados</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PLAN_TIERS.map(plan => {
                const active = planTiers.includes(plan);
                const color = PLAN_COLORS[plan];
                return (
                  <Button variant="ghost" size="sm" onClick={() => togglePlan(plan)}>
                    {plan}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Notas</label>
            <Textarea style={{ ...inputStyle(), minHeight: 60, resize: 'vertical' as const }} value={notes}
              onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} placeholder="Observações sobre o servidor..." />
          </div>

          {/* Auto setup */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24,
            cursor: 'pointer', padding: '10px 14px', borderRadius: 10,
            background: autoSetup ? '#7c5cfc15' : T.bg, border: `1px solid ${autoSetup ? '#7c5cfc44' : T.borderHover}`,
            transition: 'all 0.2s',
          }}>
            <input type="checkbox" checked={autoSetup} onChange={(e) => setAutoSetup((e.target as HTMLInputElement).checked)}
              style={{ accentColor: T.accent }} />
            <div>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>Validar automaticamente após adicionar</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Executa validação SSH imediatamente</div>
            </div>
          </label>

          {/* Submit */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button disabled={saving}>
              {saving ? 'Salvando...' : '+ Adicionar Servidor'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SETUP PROGRESS MODAL
// ══════════════════════════════════════════════════════════════════════════

const SETUP_STEPS = [
  { id: 'test_ssh', label: 'Testar conexao SSH' },
  { id: 'check_docker', label: 'Verificar Docker' },
  { id: 'install_docker', label: 'Instalar Docker' },
  { id: 'configure_daemon', label: 'Configurar daemon' },
  { id: 'setup_network', label: 'Criar rede Docker' },
  { id: 'setup_firewall', label: 'Configurar firewall' },
  { id: 'setup_fail2ban', label: 'Instalar fail2ban' },
  { id: 'harden_ssh', label: 'Hardening SSH' },
  { id: 'setup_swap', label: 'Configurar swap' },
  { id: 'verify_setup', label: 'Verificar instalacao' },
];

const VALIDATE_STEPS = [
  { id: 'test_ssh', label: 'Testar conexao SSH' },
  { id: 'system_info', label: 'Informacoes do sistema' },
  { id: 'check_docker', label: 'Verificar Docker' },
];

function SetupProgressModal({ jobId, serverName, jobType, onClose }: {
  jobId: string;
  serverName: string;
  jobType: string;
  onClose: () => void;
}) {
  const [job, setJob] = useState<ProvisioningJob | null>(null);
  const [progress, setProgress] = useState<JobProgress>({});
  const [expandedLogs, setExpandedLogs] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const steps = jobType === 'setup' ? SETUP_STEPS : VALIDATE_STEPS;

  const fetchJob = useCallback(async () => {
    try {
      const data = await api.get<{ jobs: ProvisioningJob[] }>(`/cloud/admin/servers/pending-jobs`);
      // Also try the job from server jobs
      const jobData = (data.jobs ?? []).find(j => j.id === jobId);
      if (jobData) {
        setJob(jobData);
        if (jobData.progress) {
          try { setProgress(JSON.parse(jobData.progress)); } catch { /* ignore */ }
        }
      }
      // If job is completed/failed, also try via server jobs endpoint
      if (!jobData || jobData.status === 'completed' || jobData.status === 'failed') {
        // Stop polling on completion
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    } catch { /* ignore */ }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
    timerRef.current = setInterval(fetchJob, 2000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchJob]);

  const currentStepIndex = progress.stepIndex ?? 0;
  const totalSteps = progress.totalSteps ?? steps.length;
  const pct = progress.percentage ?? Math.round((currentStepIndex / totalSteps) * 100);
  const isDone = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const isRunning = job?.status === 'running' || job?.status === 'pending';

  const getStepStatus = (idx: number): 'pending' | 'running' | 'done' | 'error' => {
    if (isFailed && idx === currentStepIndex) return 'error';
    if (idx < currentStepIndex) return 'done';
    if (idx === currentStepIndex && isRunning) return 'running';
    if (isDone) return 'done';
    return 'pending';
  };

  const stepIcons: Record<string, { icon: string; color: string }> = {
    pending: { icon: '○', color: T.textMuted },
    running: { icon: '◉', color: T.primary },
    done: { icon: '●', color: T.success },
    error: { icon: '', color: T.danger },
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
    }} onClick={onClose}>
      <div style={{
        background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 16,
        width: '90%', maxWidth: 520, padding: 24, maxHeight: '80vh', overflowY: 'auto',
      }} onClick={(e: Event) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, color: T.text, fontSize: 16, fontWeight: 700 }}>
              {jobType === 'setup' ? 'Setup Docker' : 'Validacao SSH'} - {serverName}
            </h3>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
              {isDone ? 'Concluido' : isFailed ? 'Falhou' : 'Em andamento...'}
            </div>
          </div>
          <Button variant="ghost" onClick={onClose}>x</Button>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
            <span>{currentStepIndex}/{totalSteps} etapas</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 8, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4, transition: 'width 0.3s',
              width: `${pct}%`,
              background: isFailed ? T.danger : isDone ? T.success : T.primary,
            }} />
          </div>
        </div>

        {/* Steps List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {steps.map((step, idx) => {
            const status = getStepStatus(idx);
            const { icon, color } = stepIcons[status];
            return (
              <div key={step.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: status === 'running' ? `${T.bgHover}88` : 'transparent',
                borderRadius: 8, transition: 'background 0.2s',
              }}>
                <span style={{
                  color, fontSize: 14, width: 20, textAlign: 'center' as const,
                  animation: status === 'running' ? 'pulse 1.5s infinite' : 'none',
                }}>{icon}</span>
                <span style={{
                  fontSize: 13, color: status === 'pending' ? T.textMuted : T.text,
                  fontWeight: status === 'running' ? 600 : 400,
                }}>{step.label}</span>
              </div>
            );
          })}
        </div>

        {/* Error message */}
        {isFailed && job?.error && (
          <div style={{
            marginTop: 16, padding: 12, background: '#7f1d1d22', border: `1px solid ${T.danger}44`,
            borderRadius: 8, fontSize: 12, color: T.danger, fontFamily: 'monospace',
          }}>
            {job.error}
          </div>
        )}

        {/* Logs */}
        {progress.logs && progress.logs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Button variant="ghost" size="sm" onClick={() => setExpandedLogs(!expandedLogs)}>
              {expandedLogs ? '▼' : ''} Logs ({progress.logs.length})
            </Button>
            {expandedLogs && (
              <div style={{
                marginTop: 8, padding: 12, background: T.bg, borderRadius: 8,
                fontSize: 11, fontFamily: 'monospace', color: T.textSecondary,
                maxHeight: 200, overflowY: 'auto',
              }}>
                {progress.logs.map((log, i) => (
                  <div key={i} style={{ marginBottom: 2 }}>{log}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {(isDone || isFailed) && (
            <Button variant="ghost" onClick={onClose}>Fechar</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SERVER DETAIL VIEW
// ══════════════════════════════════════════════════════════════════════════

type DetailTab = 'overview' | 'containers' | 'metrics' | 'events' | 'health' | 'jobs';

function ServerDetailView({ serverId, onBack }: { serverId: string; onBack: () => void }) {
  const [server, setServer] = useState<CloudServer | null>(null);
  const [machinesCount, setMachinesCount] = useState(0);
  const [events, setEvents] = useState<ServerEvent[]>([]);
  const [jobs, setJobs] = useState<ProvisioningJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DetailTab>('overview');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [progressModal, setProgressModal] = useState<{ jobId: string; jobType: string } | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [serverData, eventsData, jobsData] = await Promise.all([
        api.get<{ server: CloudServer; machines_count: number }>(`/cloud/admin/servers/${serverId}`),
        api.get<{ events: ServerEvent[] }>(`/cloud/admin/servers/${serverId}/events?limit=50`),
        api.get<{ jobs: ProvisioningJob[] }>(`/cloud/admin/servers/${serverId}/jobs`).catch(() => ({ jobs: [] as ProvisioningJob[] })),
      ]);
      setServer(serverData.server);
      setMachinesCount(serverData.machines_count);
      setEvents(eventsData.events);
      setJobs(jobsData.jobs);
    } catch {
      showToast('Erro ao carregar servidor', 'error');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doAction = async (action: string, label: string) => {
    setActionLoading(action);
    try {
      const result = await api.post<{ job?: ProvisioningJob }>(`/cloud/admin/servers/${serverId}/${action}`, {});
      showToast(`${label} executado com sucesso`, 'success');
      // Open progress modal for validate/setup actions
      if ((action === 'validate' || action === 'setup') && result?.job?.id) {
        setProgressModal({ jobId: result.job.id, jobType: action });
      }
      await fetchAll();
    } catch {
      showToast(`Erro ao executar ${label}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <SkeletonBar width={200} height={24} style={{ marginBottom: 20 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (!server) {
    return (
      <EmptyState icon="" title="Servidor não encontrado" desc="O servidor solicitado não existe ou foi removido."
        action="Voltar à Lista" onAction={onBack} />
    );
  }

  const lPct = loadPercent(server);
  const statusCfg = STATUS_CONFIG[server.status] ?? { color: T.textMuted, label: server.status, icon: '?' };
  const healthChecks = parseJSON<{ checks?: HealthCheck[] }>(server.health_details, {}).checks ?? [];
  const tiers = parsePlanTiers(server.plan_tiers);

  const TABS: Array<{ key: DetailTab; label: string; icon: string }> = [
    { key: 'overview', label: 'Visão Geral', icon: '' },
    { key: 'containers', label: 'Containers', icon: '' },
    { key: 'metrics', label: 'Métricas', icon: '' },
    { key: 'events', label: 'Eventos', icon: '' },
    { key: 'health', label: 'Saúde', icon: '' },
    { key: 'jobs', label: 'Jobs', icon: '' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Voltar para servidores
        </Button>

        <div style={{
          background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 16, padding: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <h2 style={{ margin: 0, color: T.text, fontSize: 22, fontWeight: 800 }}>{server.name}</h2>
                <StatusLED status={server.status} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: T.textSecondary }}>
                <span onClick={() => copyToClipboard(server.ip_address)} style={{
                  fontFamily: 'monospace', cursor: 'pointer', padding: '2px 8px',
                  background: T.bg, borderRadius: 6, border: `1px solid ${T.bgHover}`,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  transition: 'border-color 0.2s',
                }} title="Clique para copiar">
                  {server.ip_address}
                </span>
                {server.hostname && <span>{server.hostname}</span>}
                {server.provider && <span>{server.provider}</span>}
                {server.region && <span>{server.region}</span>}
                <span>{server.ssh_user}@:{server.ssh_port}</span>
              </div>
              {tiers.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                  {tiers.map(t => <PlanBadge key={t} plan={t} />)}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'validate', label: 'Validar', icon: '', color: T.primary },
                { key: 'setup', label: 'Setup', icon: '', color: T.purple },
                { key: 'health-check', label: 'Health', icon: '', color: T.success },
                { key: 'maintenance', label: 'Manutenção', icon: '', color: T.warning },
                { key: 'activate', label: 'Ativar', icon: '', color: T.success },
              ].map(a => (
                <Button variant="ghost" size="sm" onClick={() => doAction(a.key, a.label)} disabled={actionLoading !== null}>
                  {a.icon} {actionLoading === a.key ? '...' : a.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Container Load Bar */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
              <span>Carga de Containers</span>
              <span style={{ fontWeight: 600, color: loadColor(lPct) }}>{server.active_containers}/{server.max_containers} ({lPct}%)</span>
            </div>
            <ProgressBar value={server.active_containers} max={server.max_containers} height={10} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 20, overflowX: 'auto',
        background: T.bg, borderRadius: 12, padding: 4,
      }}>
        {TABS.map(t => (
          <Button variant="ghost" onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 16, padding: 24 }}>
        {tab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
              <InfoBlock label="Sistema Operacional" value={server.os_info ?? 'Não detectado'} icon="" />
              <InfoBlock label="Docker" value={server.docker_version ?? 'N/A'} icon="" />
              <InfoBlock label="Docker Compose" value={server.docker_compose_version ?? 'N/A'} icon="" />
              <InfoBlock label="CPU" value={server.cpu_cores ? `${server.cpu_cores} cores` : 'N/A'} icon="" />
              <InfoBlock label="Memória RAM" value={server.ram_mb ? `${(server.ram_mb / 1024).toFixed(1)} GB` : 'N/A'} icon="" />
              <InfoBlock label="Disco" value={server.disk_gb ? `${server.disk_gb} GB` : 'N/A'} icon="" />
              <InfoBlock label="Máquinas Vinculadas" value={String(machinesCount)} icon="" />
              <InfoBlock label="Último Health Check" value={timeAgo(server.last_health_check)} icon="" />
              <InfoBlock label="Criado em" value={new Date(server.created_at).toLocaleDateString('pt-BR')} icon="" />
              <InfoBlock label="Atualizado" value={timeAgo(server.updated_at)} icon="" />
            </div>
            {server.notes && (
              <div style={{ marginTop: 20, padding: 16, background: T.bg, borderRadius: 12, border: `1px solid ${T.bgHover}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>Notas</div>
                <div style={{ fontSize: 13, color: T.text, whiteSpace: 'pre-wrap' as const }}>{server.notes}</div>
              </div>
            )}
          </div>
        )}

        {tab === 'containers' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Carga do Servidor</span>
                <span style={{ fontSize: 13, color: loadColor(lPct), fontWeight: 700 }}>{lPct}%</span>
              </div>
              <ProgressBar value={server.active_containers} max={server.max_containers} height={12} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                <span>{server.active_containers} containers ativos</span>
                <span>{server.max_containers - server.active_containers} slots disponíveis</span>
              </div>
            </div>
            {machinesCount === 0 ? (
              <EmptyState icon="" title="Nenhum container ativo" desc="Este servidor ainda não possui containers Docker alocados." />
            ) : (
              <div style={{ fontSize: 13, color: T.textSecondary, padding: 20, textAlign: 'center' }}>
                {machinesCount} máquina(s) vinculada(s) a este servidor.
                <br />Gerencie containers na página de Máquinas Cloud.
              </div>
            )}
          </div>
        )}

        {tab === 'metrics' && (
          <div>
            {/* CSS-only resource bars */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 16px', color: T.text, fontSize: 14 }}>Uso de Recursos</h4>
              {[
                { label: 'CPU', value: server.cpu_cores ? Math.min(lPct + 10, 100) : 0, max: 100, unit: '%' },
                { label: 'RAM', value: server.ram_mb ? Math.round(server.ram_mb * (lPct / 100) * 0.8) : 0, max: server.ram_mb ?? 1, unit: 'MB' },
                { label: 'Disco', value: server.disk_gb ? Math.round(server.disk_gb * 0.3) : 0, max: server.disk_gb ?? 1, unit: 'GB' },
                { label: 'Containers', value: server.active_containers, max: server.max_containers, unit: '' },
              ].map(m => (
                <div key={m.label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                    <span>{m.label}</span>
                    <span>{m.value}{m.unit ? ` ${m.unit}` : ''} / {m.max}{m.unit ? ` ${m.unit}` : ''}</span>
                  </div>
                  <ProgressBar value={m.value} max={m.max} />
                </div>
              ))}
            </div>

            {/* Mini CSS chart - Health Check History */}
            <div>
              <h4 style={{ margin: '0 0 16px', color: T.text, fontSize: 14 }}>Histórico de Health Checks</h4>
              {events.filter(e => e.event_type === 'health_check' || e.event_type === 'status_change').length === 0 ? (
                <div style={{ textAlign: 'center', color: T.textMuted, padding: 20, fontSize: 13 }}>
                  Nenhum dado de métricas disponível ainda.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 60 }}>
                  {events.filter(e => e.event_type === 'health_check').slice(0, 20).map((ev, i) => {
                    const data = parseJSON<{ status?: string }>(ev.event_data, {});
                    const isOk = data.status === 'healthy' || data.status === 'pass';
                    return (
                      <div key={ev.id} title={`${ev.event_type} - ${new Date(ev.created_at).toLocaleString('pt-BR')}`} style={{
                        flex: 1, minWidth: 8, maxWidth: 20,
                        height: isOk ? '100%' : '40%',
                        background: isOk ? T.success : T.danger,
                        borderRadius: '3px 3px 0 0', opacity: 0.8,
                        transition: 'height 0.3s',
                      }} />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'events' && (
          <div>
            {events.length === 0 ? (
              <EmptyState icon="" title="Nenhum evento registrado" desc="Eventos serão registrados automaticamente ao executar ações." />
            ) : (
              <div style={{ position: 'relative' }}>
                {/* Timeline line */}
                <div style={{ position: 'absolute', left: 15, top: 0, bottom: 0, width: 2, background: T.borderHover }} />
                {events.map((ev, i) => {
                  const icon = EVENT_ICONS[ev.event_type] ?? '';
                  const data = ev.event_data ? (() => { try { return JSON.stringify(JSON.parse(ev.event_data), null, 0); } catch { return ev.event_data; } })() : null;
                  return (
                    <div key={ev.id} style={{
                      display: 'flex', gap: 12, paddingLeft: 4, marginBottom: 16,
                      position: 'relative',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: T.borderHover,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, flexShrink: 0, zIndex: 1, border: `2px solid ${T.bg}`,
                      }}>
                        {icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{ev.event_type.replace(/_/g, ' ')}</span>
                          <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{timeAgo(ev.created_at)}</span>
                        </div>
                        {data && (
                          <div style={{
                            fontSize: 11, color: T.textSecondary, marginTop: 4, padding: '6px 10px',
                            background: T.bg, borderRadius: 6, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
                            maxWidth: '100%', fontFamily: 'monospace',
                          }}>
                            {data.length > 200 ? data.slice(0, 200) + '...' : data}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'health' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, color: T.textSecondary }}>
                  Último check: <strong style={{ color: T.text }}>{timeAgo(server.last_health_check)}</strong>
                </div>
                <div style={{ fontSize: 12, color: HEALTH_COLORS[server.health_status] ?? T.textMuted, fontWeight: 600, marginTop: 2 }}>
                  Status: {server.health_status.toUpperCase()}
                </div>
              </div>
              <Button variant="ghost" onClick={() => doAction('health-check', 'Health Check')} disabled={actionLoading !== null}>
                {actionLoading === 'health-check' ? 'Verificando...' : 'Verificar Agora'}
              </Button>
            </div>

            {healthChecks.length === 0 ? (
              <EmptyState icon="" title="Nenhum health check disponível"
                desc="Clique em 'Verificar Agora' para executar o primeiro health check." />
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {healthChecks.map((check, i) => {
                  const color = HEALTH_COLORS[check.result] ?? T.textMuted;
                  return (
                    <div key={i} style={{
                      padding: '12px 16px', borderRadius: 10,
                      background: `${color}08`, border: `1px solid ${color}25`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{check.name}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{check.message}</div>
                      </div>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: `${color}20`, color,
                      }}>
                        {check.result.toUpperCase()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'jobs' && (
          <div>
            {jobs.length === 0 ? (
              <EmptyState icon="" title="Nenhum job de provisionamento"
                desc="Jobs são criados ao executar ações como validar, instalar ou health check." />
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {jobs.map(job => {
                  const jobStatusColors: Record<string, string> = {
                    pending: T.warning, running: T.primary, completed: T.success, failed: T.danger,
                  };
                  const jobColor = jobStatusColors[job.status] ?? T.textMuted;
                  return (
                    <div key={job.id} style={{
                      padding: '12px 16px', borderRadius: 10,
                      background: T.bg, border: `1px solid ${T.bgHover}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{job.job_type}</span>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: `${jobColor}20`, color: jobColor,
                          }}>
                            {job.status.toUpperCase()}
                          </span>
                        </div>
                        <span style={{ fontSize: 11, color: T.textMuted }}>{timeAgo(job.created_at)}</span>
                      </div>
                      {job.error && (
                        <div style={{ fontSize: 11, color: T.danger, marginTop: 4, fontFamily: 'monospace' }}>
                          Erro: {job.error.slice(0, 200)}
                        </div>
                      )}
                      {job.result && (
                        <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4, fontFamily: 'monospace' }}>
                          {(() => { try { return JSON.stringify(JSON.parse(job.result), null, 0).slice(0, 200); } catch { return job.result.slice(0, 200); } })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Setup Progress Modal */}
      {progressModal && server && (
        <SetupProgressModal
          jobId={progressModal.jobId}
          serverName={server.name}
          jobType={progressModal.jobType}
          onClose={() => { setProgressModal(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function InfoBlock({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div style={{ padding: '12px 16px', background: T.bg, borderRadius: 10, border: `1px solid ${T.bgHover}` }}>
      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span>{icon}</span> {label}
      </div>
      <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PIPELINE CONFIG TYPES & DEFAULTS (Separated: Server + Docker)
// ══════════════════════════════════════════════════════════════════════════

interface PipelineStepConfig {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  required: boolean;
  timeout: number;
  command?: string;
  config: Record<string, any>;
  enterprise?: boolean;
}

interface PipelineGeneralConfig {
  sshTimeout: number;
  maxRetries: number;
  retryBackoffMs: number;
  pollIntervalMs: number;
}

interface PipelineConfig {
  generalConfig: PipelineGeneralConfig;
  serverPipeline: PipelineStepConfig[];
  dockerPipeline: PipelineStepConfig[];
  workspacePipeline: PipelineStepConfig[];
}

const DEFAULT_SERVER_PIPELINE: PipelineStepConfig[] = [
  { id: 'test_ssh', name: 'Testar Conexão SSH', description: 'Verifica se o servidor aceita conexão SSH', enabled: true, required: true, timeout: 15, command: "echo 'SSH_OK' && hostname && uname -a", config: {} },
  { id: 'setup_firewall', name: 'Configurar Firewall (UFW)', description: 'Configura portas necessárias', enabled: true, required: false, timeout: 30, config: { ports: ['22/tcp', '80/tcp', '443/tcp', '10000:39999/tcp'] } },
  { id: 'setup_fail2ban', name: 'Instalar Fail2Ban', description: 'Proteção contra brute-force SSH', enabled: true, required: false, timeout: 60, config: { maxRetry: 5, banTime: 3600, findTime: 600 } },
  { id: 'harden_ssh', name: 'Hardening SSH', description: 'Aplica configurações de segurança no SSH', enabled: true, required: false, timeout: 30, config: { permitRootLogin: 'prohibit-password', passwordAuthentication: false, maxAuthTries: 3 } },
  { id: 'setup_swap', name: 'Configurar Swap', description: 'Cria e ativa arquivo de swap', enabled: true, required: false, timeout: 30, config: { size: '2G', swappiness: 10 } },
  { id: 'setup_logrotate', name: 'Log Rotation', description: 'Configura rotação de logs', enabled: true, required: false, timeout: 30, config: { maxSize: '10M', maxFiles: 5, rotate: 'weekly' } },
  { id: 'setup_unattended_upgrades', name: 'Updates Automáticos de Segurança', description: 'Instala unattended-upgrades para patches automáticos', enabled: true, required: false, timeout: 60, config: {} },
  { id: 'setup_ntp', name: 'Sincronização de Hora (NTP)', description: 'Instala chrony para sincronização NTP', enabled: true, required: false, timeout: 30, config: { ntpServer: 'pool.ntp.org' } },
  { id: 'setup_dedicated_user', name: 'Dedicated Docker User', description: 'Cria usuário dedicado para rodar containers — não usa root (CIS Benchmark recomenda)', enabled: true, required: false, timeout: 30, command: 'adduser --disabled-password --gecos "" dockeruser && usermod -aG docker dockeruser', config: { username: 'dockeruser' }, enterprise: true },
  { id: 'setup_apparmor', name: 'AppArmor/SELinux Profiles', description: 'Configura perfis de segurança extra no kernel para containers', enabled: true, required: false, timeout: 60, command: 'apt install -y apparmor apparmor-utils && aa-enforce /etc/apparmor.d/docker-*', config: { mode: 'enforce' }, enterprise: true },
  { id: 'setup_auditd', name: 'Auditd (Logging de Auditoria)', description: 'Instala logging de auditoria para compliance SOC2 — monitora Docker daemon, arquivos e diretórios', enabled: true, required: false, timeout: 60, command: 'apt install -y auditd && auditctl -w /usr/bin/docker -p rwxa -k docker && auditctl -w /var/lib/docker -p rwxa -k docker-files && auditctl -w /etc/docker -p rwxa -k docker-config', config: { audit_rules: '-w /usr/bin/docker -p rwxa -k docker\n-w /var/lib/docker -p rwxa -k docker-files\n-w /etc/docker -p rwxa -k docker-config' }, enterprise: true },
  { id: 'verify_server', name: 'Verificar Servidor Configurado', description: 'Verifica firewall, swap, serviços e conectividade', enabled: true, required: true, timeout: 30, command: 'ufw status && swapon --show && systemctl is-active fail2ban chrony', config: {} },
];

const DEFAULT_DOCKER_PIPELINE: PipelineStepConfig[] = [
  { id: 'check_docker', name: 'Verificar Docker Instalado', description: 'Se Docker já instalado, instalação é pulada', enabled: true, required: false, timeout: 10, command: 'docker --version 2>/dev/null && docker compose version 2>/dev/null', config: {} },
  { id: 'install_docker', name: 'Instalar Docker + Compose', description: 'Pulado se Docker já existe', enabled: true, required: false, timeout: 180, command: 'curl -fsSL https://get.docker.com | sh', config: {} },
  { id: 'configure_daemon', name: 'Configurar Docker Daemon', description: 'Escreve /etc/docker/daemon.json + systemctl restart docker', enabled: true, required: false, timeout: 30, config: { daemonJson: JSON.stringify({ 'log-driver': 'json-file', 'log-opts': { 'max-size': '10m', 'max-file': '5' }, 'storage-driver': 'overlay2', 'live-restore': true, 'default-address-pools': [{ base: '172.80.0.0/16', size: 24 }] }, null, 2) } },
  { id: 'setup_network', name: 'Criar Rede Docker', description: 'Pulado se rede já existe', enabled: true, required: false, timeout: 15, config: { networkName: 'app-cloud', subnet: '172.90.0.0/16' } },
  { id: 'setup_monitoring', name: 'Monitoramento (Node Exporter)', description: 'Coleta métricas CPU/RAM/Disk em tempo real', enabled: true, required: false, timeout: 120, config: { port: 9100 } },
  { id: 'setup_docker_cleanup', name: 'Cleanup Automático Docker', description: 'Cron para docker system prune -f', enabled: true, required: false, timeout: 30, config: { schedule: 'weekly' } },
  { id: 'setup_docker_partition', name: 'Partição /var/lib/docker', description: 'Isola disco de containers do OS — previne containers de lotar o disco raiz', enabled: true, required: false, timeout: 120, command: 'Configura mount point separado para /var/lib/docker (se disco disponível)', config: { partition_size: '40G', mount_point: '/var/lib/docker', auto_format: false }, enterprise: true },
  { id: 'verify_docker', name: 'Verificar Docker', description: 'docker run hello-world + docker info + docker compose version', enabled: true, required: true, timeout: 60, command: 'docker run hello-world && docker info && docker compose version', config: {} },
];

const DEFAULT_WORKSPACE_PIPELINE: PipelineStepConfig[] = [
  { id: 'select_server', name: 'Selecionar Servidor', description: 'Escolhe servidor por plan_tier + menor carga', enabled: true, required: true, timeout: 10, command: "Query cloud_servers WHERE status='ready' AND plan_tiers INCLUDES tier ORDER BY load ASC", config: { algorithm: 'menor_carga' } },
  { id: 'allocate_port', name: 'Alocar Porta', description: 'Reserva porta TCP no range configurado', enabled: true, required: true, timeout: 10, command: 'Busca primeira porta livre no range, marca como reservada', config: { portRangeMin: 10000, portRangeMax: 39999 } },
  { id: 'pull_image', name: 'Pull Imagem Docker', description: 'Baixa a imagem Docker do workspace', enabled: true, required: false, timeout: 300, command: 'docker pull {image_url}', config: { image: 'ghcr.io/org/workspace:latest' } },
  { id: 'create_container', name: 'Criar Container Docker', description: 'Cria e inicia o container com limites de recursos', enabled: true, required: false, timeout: 60, command: 'docker run -d --name {name} --cpus={cpu} --memory={ram} -p {port}:8080 {image}', config: { defaultCpuLimit: '1', defaultRamLimit: '512m', defaultDiskLimit: '10g' } },
  { id: 'configure_openclaw', name: 'Configurar OpenClaw', description: 'Instala e configura OpenClaw dentro do container', enabled: true, required: false, timeout: 120, config: { openclawVersion: 'latest', defaultModel: 'claude-sonnet-4-5' } },
  { id: 'setup_proxy', name: 'Configurar Proxy/URL de Acesso', description: 'Configura domínio e proxy reverso para acesso externo', enabled: true, required: false, timeout: 60, command: 'Cria config proxy + SSL cert', config: { proxyDomain: '*.workspace.yourdomain.com', proxyType: 'nginx' } },
  { id: 'configure_resources', name: 'Configurar Limites por Plano', description: 'Aplica limites de CPU/RAM/Disk baseados no plano do cliente', enabled: true, required: false, timeout: 30, config: { planLimits: JSON.stringify({ free: { cpu: '0.5', ram: '256m', disk: '5g' }, starter: { cpu: '1', ram: '512m', disk: '10g' }, pro: { cpu: '2', ram: '1g', disk: '20g' }, business: { cpu: '4', ram: '2g', disk: '50g' }, enterprise: { cpu: '8', ram: '4g', disk: '100g' } }, null, 2) } },
  { id: 'configure_backups', name: 'Configurar Backups', description: 'Define frequência de backup por plano', enabled: true, required: false, timeout: 30, config: { backupSchedules: JSON.stringify({ free: 'none', starter: 'weekly', pro: 'daily', business: 'daily', enterprise: 'hourly' }, null, 2) } },
  { id: 'health_check', name: 'Health Check do Workspace', description: 'Verifica se workspace está respondendo', enabled: true, required: true, timeout: 60, command: 'curl -f http://localhost:{port}/health', config: { healthEndpoint: '/health', expectedStatus: 200, maxWait: 60 } },
  { id: 'notify_customer', name: 'Notificar Cliente', description: 'Envia notificação ao cliente com URL de acesso', enabled: true, required: true, timeout: 15, config: { notificationTemplate: 'Seu workspace está pronto! Acesse: {url}', notificationChannels: 'email,telegram' } },
];

const DEFAULT_PIPELINE_CONFIG: PipelineConfig = {
  generalConfig: { sshTimeout: 30, maxRetries: 3, retryBackoffMs: 2000, pollIntervalMs: 5000 },
  serverPipeline: DEFAULT_SERVER_PIPELINE,
  dockerPipeline: DEFAULT_DOCKER_PIPELINE,
  workspacePipeline: DEFAULT_WORKSPACE_PIPELINE,
};

// ══════════════════════════════════════════════════════════════════════════
// PIPELINE TAB COMPONENT
// ══════════════════════════════════════════════════════════════════════════

function StepConfigFields({ step, onUpdateConfig }: { step: PipelineStepConfig; onUpdateConfig: (key: string, value: unknown) => void }) {
  const inputStyle: Record<string, string | number> = {
    padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.bgHover}`,
    background: T.bg, color: T.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  return (
    <>
      {/* configure_daemon */}
      {step.id === 'configure_daemon' && (
        <div style={{ flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>daemon.json</label>
          <Textarea value={(step.config.daemonJson as string) ?? '{}'}
            onInput={(e) => onUpdateConfig('daemonJson', (e.target as HTMLTextAreaElement).value)}
            style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 80, resize: 'vertical' as const }} />
        </div>
      )}
      {/* setup_network */}
      {step.id === 'setup_network' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Network Name</label>
          <input value={(step.config.networkName as string) ?? ''} style={{ ...inputStyle, width: 160 }}
            onInput={(e) => onUpdateConfig('networkName', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Subnet</label>
          <input value={(step.config.subnet as string) ?? ''} style={{ ...inputStyle, width: 160 }}
            onInput={(e) => onUpdateConfig('subnet', (e.target as HTMLInputElement).value)} />
        </div>
      </>)}
      {/* setup_firewall */}
      {step.id === 'setup_firewall' && (
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Portas (uma por linha)</label>
          <Textarea value={((step.config.ports as string[]) ?? []).join('\n')}
            onInput={(e) => onUpdateConfig('ports', (e.target as HTMLTextAreaElement).value.split('\n').filter(Boolean))}
            style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 60, resize: 'vertical' as const }} />
        </div>
      )}
      {/* setup_fail2ban */}
      {step.id === 'setup_fail2ban' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Max Retry</label>
          <input type="number" value={(step.config.maxRetry as number) ?? 5} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('maxRetry', parseInt((e.target as HTMLInputElement).value) || 5)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Ban Time (s)</label>
          <input type="number" value={(step.config.banTime as number) ?? 3600} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('banTime', parseInt((e.target as HTMLInputElement).value) || 3600)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Find Time (s)</label>
          <input type="number" value={(step.config.findTime as number) ?? 600} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('findTime', parseInt((e.target as HTMLInputElement).value) || 600)} />
        </div>
      </>)}
      {/* harden_ssh */}
      {step.id === 'harden_ssh' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>PermitRootLogin</label>
          <select value={(step.config.permitRootLogin as string) ?? 'prohibit-password'}
            onChange={(e) => onUpdateConfig('permitRootLogin', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 160 }}>
            <option value="prohibit-password">prohibit-password</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
            <option value="forced-commands-only">forced-commands-only</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>PasswordAuth</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: T.text }}>
            <input type="checkbox" checked={!(step.config.passwordAuthentication as boolean)}
              onChange={() => onUpdateConfig('passwordAuthentication', !(step.config.passwordAuthentication as boolean))}
              style={{ accentColor: T.success }} />
            {(step.config.passwordAuthentication as boolean) ? 'Habilitado' : 'Desabilitado'}
          </label>
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>MaxAuthTries</label>
          <input type="number" value={(step.config.maxAuthTries as number) ?? 3} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('maxAuthTries', parseInt((e.target as HTMLInputElement).value) || 3)} />
        </div>
      </>)}
      {/* setup_swap */}
      {step.id === 'setup_swap' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Tamanho</label>
          <select value={(step.config.size as string) ?? '2G'}
            onChange={(e) => onUpdateConfig('size', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 100 }}>
            {['1G', '2G', '4G', '8G'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Swappiness</label>
          <input type="number" value={(step.config.swappiness as number) ?? 10} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('swappiness', parseInt((e.target as HTMLInputElement).value) || 10)} />
        </div>
      </>)}
      {/* setup_logrotate */}
      {step.id === 'setup_logrotate' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Max Size</label>
          <input value={(step.config.maxSize as string) ?? '10M'} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('maxSize', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Max Files</label>
          <input type="number" value={(step.config.maxFiles as number) ?? 5} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('maxFiles', parseInt((e.target as HTMLInputElement).value) || 5)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Rotação</label>
          <select value={(step.config.rotate as string) ?? 'weekly'}
            onChange={(e) => onUpdateConfig('rotate', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 110 }}>
            {['daily', 'weekly', 'monthly'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </>)}
      {/* setup_monitoring */}
      {step.id === 'setup_monitoring' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Porta</label>
          <input type="number" value={(step.config.port as number) ?? 9100} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('port', parseInt((e.target as HTMLInputElement).value) || 9100)} />
        </div>
      )}
      {/* setup_docker_cleanup */}
      {step.id === 'setup_docker_cleanup' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Frequência</label>
          <select value={(step.config.schedule as string) ?? 'weekly'}
            onChange={(e) => onUpdateConfig('schedule', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 110 }}>
            {['daily', 'weekly', 'monthly'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {/* setup_ntp */}
      {step.id === 'setup_ntp' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>NTP Server</label>
          <input value={(step.config.ntpServer as string) ?? 'pool.ntp.org'} style={{ ...inputStyle, width: 180 }}
            onInput={(e) => onUpdateConfig('ntpServer', (e.target as HTMLInputElement).value)} />
        </div>
      )}
      {/* setup_dedicated_user */}
      {step.id === 'setup_dedicated_user' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Username</label>
          <input value={(step.config.username as string) ?? 'dockeruser'} style={{ ...inputStyle, width: 160 }}
            onInput={(e) => onUpdateConfig('username', (e.target as HTMLInputElement).value)} />
        </div>
      )}
      {/* setup_apparmor */}
      {step.id === 'setup_apparmor' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Mode</label>
          <select value={(step.config.mode as string) ?? 'enforce'}
            onChange={(e) => onUpdateConfig('mode', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 140 }}>
            <option value="enforce">enforce</option>
            <option value="complain">complain</option>
            <option value="disabled">disabled</option>
          </select>
        </div>
      )}
      {/* setup_auditd */}
      {step.id === 'setup_auditd' && (
        <div style={{ flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Audit Rules</label>
          <Textarea value={(step.config.audit_rules as string) ?? ''}
            onInput={(e) => onUpdateConfig('audit_rules', (e.target as HTMLTextAreaElement).value)}
            style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 80, resize: 'vertical' as const }} />
        </div>
      )}
      {/* setup_docker_partition */}
      {step.id === 'setup_docker_partition' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Partition Size</label>
          <input value={(step.config.partition_size as string) ?? '40G'} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('partition_size', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Mount Point</label>
          <input value={(step.config.mount_point as string) ?? '/var/lib/docker'} style={{ ...inputStyle, width: 180 }}
            onInput={(e) => onUpdateConfig('mount_point', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Auto Format</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: T.text }}>
            <input type="checkbox" checked={(step.config.auto_format as boolean) ?? false}
              onChange={() => onUpdateConfig('auto_format', !(step.config.auto_format as boolean))}
              style={{ accentColor: T.success }} />
            {(step.config.auto_format as boolean) ? 'Sim' : 'Não'}
          </label>
        </div>
        <div style={{ width: '100%', fontSize: 11, color: T.warning, marginTop: 4 }}>
          Requer disco adicional ou espaço livre. Se não disponível, step é pulado.
        </div>
      </>)}
      {/* allocate_port */}
      {step.id === 'allocate_port' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Port Range Min</label>
          <input type="number" value={(step.config.portRangeMin as number) ?? 10000} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('portRangeMin', parseInt((e.target as HTMLInputElement).value) || 10000)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Port Range Max</label>
          <input type="number" value={(step.config.portRangeMax as number) ?? 39999} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('portRangeMax', parseInt((e.target as HTMLInputElement).value) || 39999)} />
        </div>
      </>)}
      {/* pull_image */}
      {step.id === 'pull_image' && (
        <div style={{ flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Docker Image URL</label>
          <input value={(step.config.image as string) ?? 'ghcr.io/org/workspace:latest'} style={{ ...inputStyle }}
            onInput={(e) => onUpdateConfig('image', (e.target as HTMLInputElement).value)} />
        </div>
      )}
      {/* create_container */}
      {step.id === 'create_container' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Default CPU Limit</label>
          <input value={(step.config.defaultCpuLimit as string) ?? '1'} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('defaultCpuLimit', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Default RAM Limit</label>
          <input value={(step.config.defaultRamLimit as string) ?? '512m'} style={{ ...inputStyle, width: 100 }}
            onInput={(e) => onUpdateConfig('defaultRamLimit', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Default Disk Limit</label>
          <input value={(step.config.defaultDiskLimit as string) ?? '10g'} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('defaultDiskLimit', (e.target as HTMLInputElement).value)} />
        </div>
      </>)}
      {/* setup_proxy */}
      {step.id === 'setup_proxy' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Proxy Domain</label>
          <input value={(step.config.proxyDomain as string) ?? '*.workspace.yourdomain.com'} style={{ ...inputStyle, width: 220 }}
            onInput={(e) => onUpdateConfig('proxyDomain', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Proxy Type</label>
          <select value={(step.config.proxyType as string) ?? 'nginx'}
            onChange={(e) => onUpdateConfig('proxyType', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 110 }}>
            <option value="nginx">Nginx</option>
            <option value="caddy">Caddy</option>
            <option value="traefik">Traefik</option>
          </select>
        </div>
      </>)}
      {/* configure_backups */}
      {step.id === 'configure_backups' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Default Schedule</label>
          <select value={(step.config.defaultSchedule as string) ?? 'weekly'}
            onChange={(e) => onUpdateConfig('defaultSchedule', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 120 }}>
            {['none', 'hourly', 'daily', 'weekly'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {/* select_server */}
      {step.id === 'select_server' && (
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Algoritmo</label>
          <select value={(step.config.algorithm as string) ?? 'menor_carga'}
            onChange={(e) => onUpdateConfig('algorithm', (e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, width: 150 }}>
            <option value="menor_carga">Menor Carga</option>
            <option value="round_robin">Round Robin</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      )}
      {/* configure_openclaw */}
      {step.id === 'configure_openclaw' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Versão OpenClaw</label>
          <input value={(step.config.openclawVersion as string) ?? 'latest'} style={{ ...inputStyle, width: 120 }}
            onInput={(e) => onUpdateConfig('openclawVersion', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Modelo Padrão</label>
          <input value={(step.config.defaultModel as string) ?? 'claude-sonnet-4-5'} style={{ ...inputStyle, width: 200 }}
            onInput={(e) => onUpdateConfig('defaultModel', (e.target as HTMLInputElement).value)} />
        </div>
      </>)}
      {/* configure_resources — plan_limits JSON */}
      {step.id === 'configure_resources' && (
        <div style={{ flex: 1, minWidth: 320 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Limites por Plano (JSON)</label>
          <Textarea value={(step.config.planLimits as string) ?? '{}'}
            onInput={(e) => onUpdateConfig('planLimits', (e.target as HTMLTextAreaElement).value)}
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", minHeight: 140, resize: 'vertical' as const, background: T.bg }} />
        </div>
      )}
      {/* configure_backups — schedules JSON */}
      {step.id === 'configure_backups' && (
        <div style={{ flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Schedules por Plano (JSON)</label>
          <Textarea value={(step.config.backupSchedules as string) ?? '{}'}
            onInput={(e) => onUpdateConfig('backupSchedules', (e.target as HTMLTextAreaElement).value)}
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", minHeight: 100, resize: 'vertical' as const, background: T.bg }} />
        </div>
      )}
      {/* health_check */}
      {step.id === 'health_check' && (<>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Endpoint</label>
          <input value={(step.config.healthEndpoint as string) ?? '/health'} style={{ ...inputStyle, width: 120 }}
            onInput={(e) => onUpdateConfig('healthEndpoint', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Status Esperado</label>
          <input type="number" value={(step.config.expectedStatus as number) ?? 200} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('expectedStatus', parseInt((e.target as HTMLInputElement).value) || 200)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Max Wait (s)</label>
          <input type="number" value={(step.config.maxWait as number) ?? 60} style={{ ...inputStyle, width: 80 }}
            onInput={(e) => onUpdateConfig('maxWait', parseInt((e.target as HTMLInputElement).value) || 60)} />
        </div>
      </>)}
      {/* notify_customer */}
      {step.id === 'notify_customer' && (<>
        <div style={{ flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Template de Notificação</label>
          <input value={(step.config.notificationTemplate as string) ?? 'Seu workspace está pronto! Acesse: {url}'} style={{ ...inputStyle }}
            onInput={(e) => onUpdateConfig('notificationTemplate', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Canais (vírgula)</label>
          <input value={(step.config.notificationChannels as string) ?? 'email,telegram'} style={{ ...inputStyle, width: 180 }}
            onInput={(e) => onUpdateConfig('notificationChannels', (e.target as HTMLInputElement).value)} />
        </div>
      </>)}
    </>
  );
}

function PipelineTab() {
  const [config, setConfig] = useState<PipelineConfig>(JSON.parse(JSON.stringify(DEFAULT_PIPELINE_CONFIG)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<{ config: PipelineConfig | null }>('/cloud/admin/servers/pipeline-config');
        if (data.config?.generalConfig && (data.config?.serverPipeline?.length || data.config?.dockerPipeline?.length || data.config?.workspacePipeline?.length)) {
          setConfig(data.config);
        }
      } catch { /* use defaults */ }
      setLoading(false);
    })();
  }, []);

  const updateGeneral = (field: keyof PipelineGeneralConfig, value: number) => {
    setConfig(prev => ({ ...prev, generalConfig: { ...prev.generalConfig, [field]: value } }));
    setDirty(true);
  };

  const updateStep = (pipeline: 'serverPipeline' | 'dockerPipeline' | 'workspacePipeline', idx: number, patch: Partial<PipelineStepConfig>) => {
    setConfig(prev => {
      const steps = [...prev[pipeline]];
      steps[idx] = { ...steps[idx], ...patch } as PipelineStepConfig;
      return { ...prev, [pipeline]: steps };
    });
    setDirty(true);
  };

  const updateStepConfig = (pipeline: 'serverPipeline' | 'dockerPipeline' | 'workspacePipeline', idx: number, key: string, value: unknown) => {
    setConfig(prev => {
      const steps = [...prev[pipeline]];
      steps[idx] = { ...steps[idx], config: { ...steps[idx].config, [key]: value } } as PipelineStepConfig;
      return { ...prev, [pipeline]: steps };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cloud/admin/servers/pipeline-config', config);
      showToast('Pipeline salvo com sucesso!', 'success');
      setDirty(false);
    } catch {
      showToast('Erro ao salvar pipeline', 'error');
    }
    setSaving(false);
  };

  const handleReset = () => {
    setConfig(JSON.parse(JSON.stringify(DEFAULT_PIPELINE_CONFIG)));
    setDirty(true);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><SkeletonCard /><div style={{ marginTop: 12 }}><SkeletonCard /></div></div>;

  const inputStyle: Record<string, string | number> = {
    padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.bgHover}`,
    background: T.bg, color: T.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  const renderStepCards = (
    steps: PipelineStepConfig[],
    pipeline: 'serverPipeline' | 'dockerPipeline' | 'workspacePipeline',
    accentColor: string,
  ) => (
    <div style={{ display: 'grid', gap: 10 }}>
      {steps.map((step, idx) => {
        const borderColor = !step.enabled ? T.textDisabled : step.required ? accentColor : accentColor;
        return (
          <div key={step.id} style={{
            background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12,
            padding: '16px 20px', borderLeft: `3px solid ${!step.enabled ? T.textDisabled : borderColor}`,
            opacity: step.enabled ? 1 : 0.6, transition: 'opacity 0.2s',
          }}>
            {/* Step Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 28, height: 28, borderRadius: '50%', background: T.borderHover,
                  color: T.textSecondary, fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{idx + 1}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{step.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>{step.description}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {step.enterprise && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${T.warning}20`, color: T.warning }}>
                    Enterprise
                  </span>
                )}
                {step.required && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#D9970620', color: '#D99706' }}>
                    OBRIGATÓRIO
                  </span>
                )}
                <label style={{ display: 'flex', alignItems: 'center', cursor: step.required ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={step.enabled} disabled={step.required}
                    onChange={() => !step.required && updateStep(pipeline, idx, { enabled: !step.enabled })}
                    style={{ accentColor: T.success }} />
                </label>
              </div>
            </div>

            {/* Command block */}
            {step.command && (
              <div style={{
                background: T.bg, border: `1px solid ${T.bgHover}`, borderRadius: 8,
                padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                color: T.success, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' as const,
              }}>
                $ {step.command}
              </div>
            )}

            {/* Step-specific configs */}
            {step.enabled && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ minWidth: 100 }}>
                  <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Timeout (s)</label>
                  <input type="number" value={step.timeout} style={{ ...inputStyle, width: 100 }}
                    onInput={(e) => updateStep(pipeline, idx, { timeout: parseInt((e.target as HTMLInputElement).value) || 15 })} />
                </div>
                <StepConfigFields step={step} onUpdateConfig={(key, value) => updateStepConfig(pipeline, idx, key, value)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', color: T.text, fontSize: 18, fontWeight: 700 }}>Pipeline de Provisionamento</h3>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>
          Configure os steps executados ao provisionar um novo servidor. Duas pipelines separadas: Servidor e Docker.
        </p>
      </div>

      {/* General Config Card */}
      <div style={{ background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          Configurações Gerais
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {([
            ['sshTimeout', 'SSH Timeout (s)', config.generalConfig.sshTimeout],
            ['maxRetries', 'Max Retries', config.generalConfig.maxRetries],
            ['retryBackoffMs', 'Retry Backoff (ms)', config.generalConfig.retryBackoffMs],
            ['pollIntervalMs', 'Poll Interval (ms)', config.generalConfig.pollIntervalMs],
          ] as const).map(([field, label, val]) => (
            <div key={field}>
              <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>{label}</label>
              <input type="number" value={val} style={inputStyle}
                onInput={(e) => updateGeneral(field as keyof PipelineGeneralConfig, parseInt((e.target as HTMLInputElement).value) || 0)} />
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ Pipeline 1: Servidor ═══════ */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
          <span style={{ fontSize: 24 }}></span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: T.text }}>Pipeline: Instalação do Servidor</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.textSecondary }}>Configura segurança, firewall e infraestrutura base</p>
          </div>
          <span style={{ background: T.borderHover, color: T.textSecondary, padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
            {config.serverPipeline.length} steps
          </span>
          <Button variant="ghost" disabled>
            Executar Pipeline Servidor
          </Button>
        </div>
        {renderStepCards(config.serverPipeline, 'serverPipeline', T.primary)}
      </div>

      {/* ═══════ Divider ═══════ */}
      <div style={{ borderTop: `1px solid ${T.bgHover}`, margin: '32px 0' }} />

      {/* ═══════ Pipeline 2: Docker ═══════ */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
          <span style={{ fontSize: 24 }}></span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: T.text }}>Pipeline: Instalação do Docker</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.textSecondary }}>Instala e configura Docker Engine, rede e monitoramento</p>
          </div>
          <span style={{ background: T.borderHover, color: T.textSecondary, padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
            {config.dockerPipeline.length} steps
          </span>
          <Button variant="ghost" disabled>
            Executar Pipeline Docker
          </Button>
        </div>
        {renderStepCards(config.dockerPipeline, 'dockerPipeline', T.accent)}
      </div>

      {/* ═══════ Divider ═══════ */}
      <div style={{ borderTop: `1px solid ${T.bgHover}`, margin: '32px 0' }} />

      {/* ═══════ Pipeline 3: Workspace ═══════ */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
          <span style={{ fontSize: 24 }}></span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: T.text }}>Pipeline: Criação de Workspace</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.textSecondary }}>Cria e configura container Docker para cada cliente</p>
          </div>
          <span style={{ background: T.borderHover, color: T.textSecondary, padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
            {config.workspacePipeline.length} steps
          </span>
          <Button variant="ghost" disabled>
            Executar Pipeline Workspace
          </Button>
        </div>
        {renderStepCards(config.workspacePipeline, 'workspacePipeline', T.success)}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.bgHover}` }}>
        <Button variant="ghost" onClick={handleReset}>
          Resetar Padrões
        </Button>
        <Button onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Salvando...' : dirty ? 'Salvar Configurações' : 'Salvo'}
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MONITORING TAB COMPONENT
// ══════════════════════════════════════════════════════════════════════════

interface MonitoringSummary {
  activeJobs: number;
  pendingJobs: number;
  completedLast24h: number;
  failedLast24h: number;
}

interface MonitoringJob extends ProvisioningJob {
  server_name: string;
  server_ip: string;
}

interface MonitoringEvent extends ServerEvent {
  server_name: string;
  server_ip: string;
}

function MonitoringTab() {
  const [summary, setSummary] = useState<MonitoringSummary | null>(null);
  const [activeJobs, setActiveJobs] = useState<MonitoringJob[]>([]);
  const [jobHistory, setJobHistory] = useState<MonitoringJob[]>([]);
  const [jobHistoryTotal, setJobHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [expandedHistoryJobs, setExpandedHistoryJobs] = useState<Set<string>>(new Set());
  const [fullscreenJob, setFullscreenJob] = useState<string | null>(null);
  const [showLogsFor, setShowLogsFor] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const fetchData = useCallback(async (isInitial = false) => {
    try {
      const [summaryData, jobsData] = await Promise.all([
        api.get<MonitoringSummary>('/cloud/admin/servers/monitoring/summary'),
        api.get<{ jobs: MonitoringJob[] }>('/cloud/admin/servers/monitoring/active-jobs'),
      ]);
      setSummary(summaryData);
      setActiveJobs(jobsData.jobs);

      if (isInitial) {
        const histData = await api.get<{ jobs: MonitoringJob[]; total: number }>('/cloud/admin/servers/monitoring/job-history?limit=50&offset=0');
        setJobHistory(histData.jobs);
        setJobHistoryTotal(histData.total);
      }
    } catch {
      if (isInitial) showToast('Erro ao carregar monitoramento', 'error');
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(true); }, []);

  // 2s polling when active jobs, 10s otherwise
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      const interval = (activeJobs.length > 0) ? 2000 : 10000;
      intervalRef.current = setInterval(() => fetchData(false), interval);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, activeJobs.length, fetchData]);

  // Auto-scroll logs
  useEffect(() => {
    Object.values(logRefs.current).forEach(el => {
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [activeJobs]);

  const toggleLogs = (id: string) => {
    setShowLogsFor(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleHistoryJob = (id: string) => {
    setExpandedHistoryJobs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRetry = async (serverId: string) => {
    setRetrying(serverId);
    try {
      await api.post(`/cloud/admin/servers/${serverId}/setup`, {});
      showToast('Setup reiniciado', 'success');
      setTimeout(() => fetchData(true), 1000);
    } catch {
      showToast('Erro ao reiniciar setup', 'error');
    }
    setRetrying(null);
  };

  const formatElapsed = (startedAt: string | null) => {
    if (!startedAt) return '--:--';
    const diff = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  };

  const formatDuration = (startedAt: string | null, completedAt: string | null) => {
    if (!startedAt || !completedAt) return '-';
    const diff = Math.floor((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000);
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Parse progress from the progress JSON field or result/payload steps
  const getJobProgress = (job: MonitoringJob): {
    currentStep: string | null;
    stepIndex: number;
    totalSteps: number;
    percentage: number;
    logs: string[];
    steps: Array<{ name: string; status: string; message?: string; duration?: number; timestamp?: string }>;
  } => {
    // Try progress field first (real-time updates)
    const progress = parseJSON<{ currentStep?: string; stepIndex?: number; totalSteps?: number; percentage?: number; logs?: string[] }>(job.progress, {});
    // Then result/payload for completed jobs
    const result = parseJSON<{ steps?: Array<Record<string, unknown>> }>(job.result, {});
    const payload = parseJSON<{ steps?: Array<Record<string, unknown>> }>(job.payload, {});
    const steps = (result.steps ?? payload.steps ?? []) as Array<{ name: string; status: string; message?: string; duration?: number; timestamp?: string }>;

    if (progress.totalSteps && progress.totalSteps > 0) {
      return {
        currentStep: progress.currentStep ?? null,
        stepIndex: progress.stepIndex ?? 0,
        totalSteps: progress.totalSteps,
        percentage: progress.percentage ?? Math.round(((progress.stepIndex ?? 0) / progress.totalSteps) * 100),
        logs: progress.logs ?? [],
        steps,
      };
    }

    // Fallback to steps
    const done = steps.filter(s => s.status === 'completed' || s.status === 'success' || s.status === 'skipped').length;
    const total = steps.length || 1;
    return {
      currentStep: steps.find(s => s.status === 'running')?.name ?? null,
      stepIndex: done,
      totalSteps: total,
      percentage: Math.round((done / total) * 100),
      logs: progress.logs ?? [],
      steps,
    };
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': case 'success': return <IconCheck size={14} color={T.success} />;
      case 'failed': case 'error': return <IconX size={14} color={T.danger} />;
      case 'running': return <IconPulse size={14} color={T.warning} />;
      case 'skipped': return <IconSkip size={14} color={T.textMuted} />;
      default: return <IconCircle size={14} color={T.textDisabled} />;
    }
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed': case 'success': return T.success;
      case 'failed': case 'error': return T.danger;
      case 'running': return T.warning;
      case 'skipped': return T.textMuted;
      default: return T.textDisabled;
    }
  };

  const copyLogs = (job: MonitoringJob) => {
    const { steps, logs } = getJobProgress(job);
    const lines = steps.map((s, i) =>
      `[${s.name}] ${s.status === 'completed' || s.status === 'success' ? 'OK' : s.status === 'failed' || s.status === 'error' ? 'FAIL' : s.status === 'skipped' ? 'SKIP' : s.status.toUpperCase()}${s.duration ? ` (${formatMs(s.duration)})` : ''}${s.message ? ` — ${s.message}` : ''}`
    ).join('\n');
    const logLines = logs.length > 0 ? '\n\n--- LOGS ---\n' + logs.join('\n') : '';
    navigator.clipboard.writeText((lines || 'Sem logs disponíveis') + logLines).then(
      () => showToast('Logs copiados!', 'success'),
      () => showToast('Erro ao copiar', 'error'),
    );
  };

  const JOB_TYPE_LABELS: Record<string, string> = { validate: 'Validate Server', setup: 'Setup Docker', provision: 'Provision Container', health_check: 'Health Check' };
  const JOB_STATUS_COLORS: Record<string, string> = { pending: T.warning, running: T.primary, completed: T.success, failed: T.danger };
  const JOB_STATUS_LABELS: Record<string, string> = { pending: 'Pendente', running: 'Executando', completed: 'Concluido', failed: 'Falhou' };

  if (loading) {
    return (
      <div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
        <SkeletonCard />
      </div>
    );
  }

  // Fullscreen log overlay
  if (fullscreenJob) {
    const job = [...activeJobs, ...jobHistory].find(j => j.id === fullscreenJob);
    if (job) {
      const { steps, logs } = getJobProgress(job);
      return (
        <div style={{
          position: 'fixed', inset: 0, background: T.bg, zIndex: 1000,
          display: 'flex', flexDirection: 'column', padding: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontSize: 14, fontWeight: 600 }}>
              <IconServer size={16} color="#7c5cfc" />
              <span>{job.server_name ?? 'Server'} — {JOB_TYPE_LABELS[job.job_type] ?? job.job_type}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFullscreenJob(null)}>
              <IconX size={12} color={T.textSecondary} /> Fechar
            </Button>
          </div>
          <div style={{
            flex: 1, overflow: 'auto', fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: 12, lineHeight: 1.8, background: T.bg, borderRadius: 12,
            border: `1px solid ${T.bgHover}`, padding: 20,
          }}>
            {steps.length === 0 && logs.length === 0 && <div style={{ color: T.textMuted }}>Aguardando logs...</div>}
            {steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                {getStepIcon(step.status)}
                <span style={{ color: T.textMuted, minWidth: 140 }}>{step.name}</span>
                <span style={{ color: getStepColor(step.status), flex: 1 }}>
                  {step.status === 'running' ? 'running...' : step.status === 'skipped' ? 'SKIP' : step.message ?? step.status.toUpperCase()}
                </span>
                {step.duration != null && <span style={{ color: T.textMuted, fontSize: 11 }}>{formatMs(step.duration)}</span>}
              </div>
            ))}
            {logs.length > 0 && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${T.bgHover}`, paddingTop: 12 }}>
                {logs.map((log, i) => (
                  <div key={i} style={{ color: T.textSecondary, padding: '1px 0' }}>{log}</div>
                ))}
              </div>
            )}
            {job.error && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 8, border: `${T.danger}15`, color: T.danger }}>
                {job.error}
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <div>
      {/* CSS */}
      <style>{`
        @keyframes pulseGlow { 0%, 100% { box-shadow: 0 0 8px ${T.success}44; } 50% { box-shadow: 0 0 20px ${T.success}88; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: '0 0 4px', color: T.text, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconActivity size={20} color="#7c5cfc" /> Pipeline Monitoring
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>
            Acompanhe pipelines em tempo real — steps, logs e progresso
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {autoRefresh && activeJobs.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: T.success,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <span style={{ color: T.success }}>LIVE (2s)</span>
            </div>
          ) : autoRefresh ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.textMuted }} />
              <span style={{ color: T.textMuted }}>Idle (10s)</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.danger }} />
              <span style={{ color: T.danger }}>Pausado</span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
            <IconRefresh size={12} color={autoRefresh ? T.success : T.textSecondary} />
            {autoRefresh ? 'Pausar' : 'Iniciar'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => fetchData(true)}>
            <IconRefresh size={12} /> Atualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <div style={{
          background: 'linear-gradient(135deg, #2F2F2F 0%, #363636 100%)',
          border: activeJobs.length > 0 ? `1px solid ${T.success}44` : `1px solid ${T.bgHover}`,
          borderRadius: 12, padding: '16px 20px',
          animation: activeJobs.length > 0 ? 'pulseGlow 2s ease-in-out infinite' : undefined,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <IconActivity size={16} color={T.success} />
            <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Jobs Ativos</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: activeJobs.length > 0 ? T.success : T.textMuted, lineHeight: 1 }}>{activeJobs.length}</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #2F2F2F 0%, #363636 100%)', border: `1px solid ${T.whiteAlpha8}`, borderRadius: 12, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <IconCheck size={16} color={T.success} />
            <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Completos (24h)</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.success, lineHeight: 1 }}>{summary?.completedLast24h ?? 0}</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #2F2F2F 0%, #363636 100%)', border: `1px solid ${T.whiteAlpha8}`, borderRadius: 12, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <IconX size={16} color={T.danger} />
            <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Falhados (24h)</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: summary?.failedLast24h ? T.danger : T.textMuted, lineHeight: 1 }}>{summary?.failedLast24h ?? 0}</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #2F2F2F 0%, #363636 100%)', border: `1px solid ${T.whiteAlpha8}`, borderRadius: 12, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <IconClock size={16} color={T.warning} />
            <span style={{ fontSize: 11, color: T.textSecondary, fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>Pendentes</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: summary?.pendingJobs ? T.warning : T.textMuted, lineHeight: 1 }}>{summary?.pendingJobs ?? 0}</div>
        </div>
      </div>

      {/* Active Jobs Section */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 12px', color: T.text, fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconActivity size={16} color={T.primary} /> Jobs Ativos
          {activeJobs.length > 0 && <span style={{ fontSize: 11, color: T.primary, background: `${T.primary}20`, padding: '2px 8px', borderRadius: 10 }}>{activeJobs.length}</span>}
        </h4>

        {activeJobs.length === 0 ? (
          <EmptyState icon="" title="Nenhum job ativo" desc="Todos os pipelines foram concluidos. Sistema em repouso." />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {activeJobs.map(job => {
              const prog = getJobProgress(job);
              const statusColor = JOB_STATUS_COLORS[job.status] ?? T.textMuted;
              const logsVisible = showLogsFor.has(job.id);

              return (
                <div key={job.id} style={{
                  background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12, overflow: 'hidden',
                  borderLeft: `3px solid ${statusColor}`,
                }}>
                  <div style={{ padding: '20px 24px' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                          background: `${statusColor}20`, color: statusColor, textTransform: 'uppercase' as const,
                        }}>
                          {JOB_STATUS_LABELS[job.status] ?? job.status}
                        </span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                          {JOB_TYPE_LABELS[job.job_type] ?? job.job_type}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSecondary }}>
                        <IconClock size={12} color={T.textSecondary} />
                        <span style={{ fontFamily: 'monospace' }}>{formatElapsed(job.started_at)}</span>
                      </div>
                    </div>

                    {/* Server info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 13, color: T.textSecondary }}>
                      <IconServer size={14} color={T.textMuted} />
                      <span>Servidor: <strong style={{ color: T.text }}>{job.server_name ?? 'Unknown'}</strong></span>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: T.textMuted }}>({job.server_ip ?? ''})</span>
                      <span style={{ marginLeft: 8, fontSize: 11, color: T.textMuted }}>
                        Iniciado: {job.started_at ? new Date(job.started_at).toLocaleString('pt-BR') : 'Aguardando'}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {prog.currentStep ? (
                            <><IconPulse size={10} color={T.warning} /> {prog.currentStep}</>
                          ) : prog.percentage === 100 ? (
                            <><IconCheck size={10} color={T.success} /> Concluido</>
                          ) : 'Aguardando...'}
                        </span>
                        <span style={{ fontWeight: 600, color: T.text }}>{prog.percentage}% ({prog.stepIndex}/{prog.totalSteps})</span>
                      </div>
                      <div style={{ height: 10, borderRadius: 5, background: T.borderHover, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 5, width: `${prog.percentage}%`,
                          background: 'linear-gradient(90deg, #2F6BFF, #7c5cfc)',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>

                    {/* Steps list */}
                    {prog.steps.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Steps:</div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          {prog.steps.map((step, i) => (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
                              borderRadius: 6,
                              background: step.status === 'running' ? `${T.warning}08` : `transparent`,
                            }}>
                              {getStepIcon(step.status)}
                              <span style={{
                                flex: 1, fontSize: 12,
                                color: step.status === 'running' ? T.warning : step.status === 'completed' || step.status === 'success' ? T.text : getStepColor(step.status),
                                fontWeight: step.status === 'running' ? 600 : 400,
                              }}>
                                {step.name}
                              </span>
                              <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.textMuted, minWidth: 60, textAlign: 'right' as const }}>
                                {step.status === 'running' ? 'running...' : step.status === 'skipped' ? 'SKIP' : step.duration != null ? formatMs(step.duration) : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => toggleLogs(job.id)}>
                        {logsVisible ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
                        <IconLogs size={11} /> {logsVisible ? 'Ocultar Logs' : 'Ver Logs'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyLogs(job)}>
                        <IconCopy size={11} /> Copiar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setFullscreenJob(job.id)}>
                        <IconExpand size={11} /> Expandir
                      </Button>
                    </div>
                  </div>

                  {/* Log console */}
                  {logsVisible && (
                    <div ref={el => { logRefs.current[job.id] = el; }} style={{
                      background: T.bg, borderTop: `1px solid ${T.bgHover}`, padding: 16,
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: 12, lineHeight: 1.7, maxHeight: 400, overflowY: 'auto',
                      scrollBehavior: 'smooth',
                    }}>
                      {prog.steps.length === 0 && prog.logs.length === 0 && (
                        <div style={{ color: T.textMuted }}>Aguardando logs...</div>
                      )}
                      {prog.steps.map((step, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                          <span style={{ color: T.textDisabled, fontSize: 11, minWidth: 30 }}>{String(i + 1).padStart(2, '0')}</span>
                          <span style={{ color: getStepColor(step.status), fontWeight: 500 }}>
                            [{step.name}] {step.status === 'running' ? 'running...' : step.status === 'skipped' ? 'SKIP' : `${step.status.toUpperCase()}${step.duration != null ? ` (${formatMs(step.duration)})` : ''}`}
                          </span>
                        </div>
                      ))}
                      {prog.logs.map((log, i) => (
                        <div key={`log-${i}`} style={{ color: T.textMuted, padding: '1px 0', marginLeft: 38 }}>{log}</div>
                      ))}
                      {job.error && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: `${T.danger}10`, borderRadius: 6, color: T.danger, borderLeft: `${T.danger}10` }}>
                          ERROR: {job.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Failed Jobs with Retry */}
      {jobHistory.filter(j => j.status === 'failed').slice(0, 5).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 12px', color: T.text, fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconX size={16} color={T.danger} /> Jobs com Erro
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {jobHistory.filter(j => j.status === 'failed').slice(0, 5).map(job => {
              const prog = getJobProgress(job);
              const failedStep = prog.steps.find(s => s.status === 'failed' || s.status === 'error');
              return (
                <div key={job.id} style={{
                  background: `${T.border}`, border: `1px solid ${T.danger}33`, borderRadius: 12,
                  padding: '16px 20px', borderLeft: `3px solid ${T.danger}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconServer size={14} color={T.textMuted} />
                        {job.server_name ?? 'Server'} <span style={{ fontSize: 12, color: T.textMuted }}>({job.server_ip})</span>
                      </div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                        {JOB_TYPE_LABELS[job.job_type] ?? job.job_type} — {prog.stepIndex}/{prog.totalSteps} steps
                      </div>
                      {failedStep && (
                        <div style={{ fontSize: 12, color: T.danger }}>
                          Step falhou: <strong>{failedStep.name}</strong>{failedStep.message ? ` — ${failedStep.message}` : ''}
                        </div>
                      )}
                      {job.error && !failedStep && (
                        <div style={{ fontSize: 12, color: T.danger }}>{job.error}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => setFullscreenJob(job.id)}>
                        <IconLogs size={11} /> Logs
                      </Button>
                      <Button size="sm" onClick={() => handleRetry(job.server_id)} disabled={retrying === job.server_id}>
                        <IconRetry size={11} color="#fff" /> {retrying === job.server_id ? 'Retrying...' : 'Retry Setup'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job History Table */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 12px', color: T.text, fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconClock size={16} color={T.textSecondary} /> Historico de Jobs
          <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}>({jobHistoryTotal} total)</span>
        </h4>

        <div style={{ background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.bgHover}` }}>
                  {['Status', 'Tipo', 'Servidor', 'Duracao', 'Steps', 'Data', ''].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left' as const, color: T.textMuted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase' as const }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobHistory.map(job => {
                  const statusColor = JOB_STATUS_COLORS[job.status] ?? T.textMuted;
                  const prog = getJobProgress(job);
                  const isExpanded = expandedHistoryJobs.has(job.id);
                  return (
                    <>
                      <tr key={job.id} onClick={() => toggleHistoryJob(job.id)} style={{ borderBottom: `1px solid ${T.bgHover}44`, cursor: 'pointer' }}
                        onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.background = `${T.bgHover}44`; }}
                        onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {job.status === 'completed' ? <IconCheck size={12} color={T.success} /> : job.status === 'failed' ? <IconX size={12} color={T.danger} /> : job.status === 'running' ? <IconPulse size={10} color={T.primary} /> : <IconCircle size={12} color={T.warning} />}
                            <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${statusColor}15`, color: statusColor }}>
                              {JOB_STATUS_LABELS[job.status] ?? job.status}
                            </span>
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', color: T.text }}>{JOB_TYPE_LABELS[job.job_type] ?? job.job_type}</td>
                        <td style={{ padding: '8px 12px', color: T.text }}>{job.server_name ?? '-'} <span style={{ color: T.textMuted, fontSize: 11 }}>({job.server_ip})</span></td>
                        <td style={{ padding: '8px 12px', color: T.textSecondary, fontFamily: 'monospace' }}>{formatDuration(job.started_at, job.completed_at)}</td>
                        <td style={{ padding: '8px 12px', color: T.textSecondary }}>{prog.stepIndex}/{prog.totalSteps}</td>
                        <td style={{ padding: '8px 12px', color: T.textMuted }}>{job.created_at ? new Date(job.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          {isExpanded ? <IconChevronUp size={10} color={T.textMuted} /> : <IconChevronDown size={10} color={T.textMuted} />}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${job.id}-detail`}>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div style={{
                              background: T.bg, padding: 16, borderBottom: `1px solid ${T.bgHover}`,
                              fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12, lineHeight: 1.7,
                            }}>
                              {prog.steps.length === 0 && <div style={{ color: T.textMuted }}>Sem detalhes de steps</div>}
                              {prog.steps.map((step, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                                  {getStepIcon(step.status)}
                                  <span style={{ color: getStepColor(step.status), minWidth: 140 }}>{step.name}</span>
                                  <span style={{ color: T.textMuted, flex: 1 }}>
                                    {step.status === 'skipped' ? 'SKIP' : step.message ?? step.status}
                                  </span>
                                  {step.duration != null && <span style={{ color: T.textMuted }}>{formatMs(step.duration)}</span>}
                                </div>
                              ))}
                              {job.error && (
                                <div style={{ marginTop: 8, color: T.danger, padding: '4px 8px', background: `${T.danger}10`, borderRadius: 6 }}>
                                  Error: {job.error}
                                </div>
                              )}
                              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                                <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); setFullscreenJob(job.id); }}>
                                  <IconExpand size={10} /> Fullscreen
                                </Button>
                                <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); copyLogs(job); }}>
                                  <IconCopy size={10} /> Copiar
                                </Button>
                                {job.status === 'failed' && (
                                  <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); handleRetry(job.server_id); }}>
                                    <IconRetry size={10} color="#000" /> Retry
                                  </Button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// DOCKER TAB
// ══════════════════════════════════════════════════════════════════════════

// ── Shared types & helpers ──

interface DockerStatsData {
  totals: { total: number; running: number; provisioning: number; stopped_or_error: number };
  byStatus: Array<{ status: string; count: number }>;
  byServer: Array<{ server_name: string; server_id: string; ip_address: string; max_containers: number; container_count: number; running_count: number }>;
  byType: Array<{ workspace_type: string; count: number }>;
  recentContainers: Array<{ id: string; name: string; display_name: string; status: string; server_id: string; created_at: string }>;
}

interface DockerContainer {
  id: string; name: string; display_name: string; status: string; server_id: string;
  container_name: string; container_docker_id: string; openclaw_port: number; api_port: number;
  resource_limits: string; workspace_type: string; customer_id: string;
  created_at: string; updated_at: string; provisioning_started_at: string; provisioning_completed_at: string;
  server_name: string; server_ip: string;
}

interface CloudContainer {
  id: string; name: string; status: string; server_id: string; customer_id: string;
  container_docker_id: string; image: string; ports: string; resources: string;
  environment: string; volumes: string; network: string; domain: string;
  plan_tier: string; provisioning_job_id: string; started_at: string; stopped_at: string;
  created_at: string; updated_at: string; server_name: string; server_ip: string;
}

interface ContainerStats {
  totals: { total: number; running: number; stopped: number; error: number };
  byServer: Array<{ server_id: string; server_name: string; ip_address: string; container_count: number; running_count: number }>;
}

interface DockerImage {
  id: string; name: string; tag: string; registry: string; size_mb: number; created_at: string; fallback_url?: string;
}

const containerStatusColor = (s: string) => {
  if (s === 'running') return T.success;
  if (s === 'provisioning') return T.warning;
  if (s === 'stopped') return T.textMuted;
  if (s === 'error' || s === 'failed') return T.danger;
  return T.textMuted;
};

const containerStatusLabel = (s: string) => {
  const map: Record<string, string> = { running: 'Rodando', provisioning: 'Provisionando', stopped: 'Parado', error: 'Erro', failed: 'Falhou', pending: 'Pendente' };
  return map[s] || s;
};

// ══════════════════════════════════════════════════════════════════════════
// PROVISIONING TAB
// ══════════════════════════════════════════════════════════════════════════

interface ProvStatus {
  configured: boolean;
  config?: { ip: string; sshPort: number; healthPort: number; label: string; serverId: string };
  server?: Record<string, unknown> | null;
  health: { status: string; latencyMs?: number; data?: unknown };
  tunnel: { status: string; latencyMs?: number };
  jobs: { total: number; running: number; pending: number; completed_24h: number; failed_24h: number; recent: Array<Record<string, unknown>> };
  events: Array<Record<string, unknown>>;
  checkedAt: string;
}

function ProvisioningTab() {
  const [data, setData] = useState<ProvStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const d = await api.get<ProvStatus>('/cloud/admin/servers/provisioning/status');
      setData(d);
    } catch { showToast('Erro ao carregar status', 'error'); }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><ListSkeleton rows={3} /></div>;

  if (!data?.configured) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}></div>
        <div style={{ fontSize: 16, color: T.text, fontWeight: 700, marginBottom: 8 }}>Servidor de Provisionamento não configurado</div>
        <div style={{ fontSize: 13, color: T.textMuted }}>Vá em Configurações para definir o servidor de provisionamento</div>
      </div>
    );
  }

  const srv = data.server as Record<string, unknown> | null;
  const healthColor = data.health.status === 'healthy' ? T.success : data.health.status === 'unreachable' ? T.danger : T.warning;
  const tunnelColor = data.tunnel.status === 'healthy' ? T.success : data.tunnel.status === 'unreachable' ? T.danger : T.warning;
  const healthLabel = data.health.status === 'healthy' ? 'Online' : data.health.status === 'unreachable' ? 'Inacessível' : 'Degradado';
  const tunnelLabel = data.tunnel.status === 'healthy' ? 'Ativo' : data.tunnel.status === 'unreachable' ? 'Inacessível' : 'Degradado';

  const cardStyle: JSX.CSSProperties = { background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 16 };
  const kpiStyle = (color: string): JSX.CSSProperties => ({ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: '16px 20px', flex: 1, minWidth: 120, textAlign: 'center' });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', color: T.text, fontSize: 22, fontWeight: 800 }}>Servidor de Provisionamento</h2>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>
            {data.config?.label || 'Agent Principal'} — {data.config?.ip}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {refreshing && <span style={{ fontSize: 12, color: T.textMuted }}></span>}
          <span style={{ fontSize: 11, color: T.textMuted }}>Atualizado: {data.checkedAt ? new Date(data.checkedAt).toLocaleTimeString('pt-BR') : '—'}</span>
          <Button variant="ghost" size="sm" onClick={() => fetchStatus(true)}>Atualizar</Button>
        </div>
      </div>

      {/* ── Status Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {/* Agent Health */}
        <div style={{ ...cardStyle, border: `1px solid ${healthColor}33` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Health Check — Agent</span>
            <span style={{ fontSize: 12, color: healthColor, fontWeight: 700 }}>{healthLabel}</span>
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: T.textSecondary }}>
            <span>Endpoint: <code style={{ color: T.accent }}>http://{data.config?.ip}:{data.config?.healthPort || 18790}/health</code></span>
          </div>
          {data.health.latencyMs !== undefined && (
            <div style={{ marginTop: 8, fontSize: 12, color: T.textSecondary }}>
              Latência: <span style={{ color: data.health.latencyMs < 200 ? T.success : data.health.latencyMs < 500 ? T.warning : T.danger, fontWeight: 600 }}>{data.health.latencyMs}ms</span>
            </div>
          )}
          {data.health.data && (
            <pre style={{ marginTop: 8, fontSize: 11, color: T.textMuted, background: T.bg, padding: 10, borderRadius: 6, overflow: 'auto', maxHeight: 120 }}>
              {JSON.stringify(data.health.data, null, 2)}
            </pre>
          )}
        </div>

        {/* Tunnel Status */}
        <div style={{ ...cardStyle, border: `1px solid ${tunnelColor}33` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Cloudflare Tunnel</span>
            <span style={{ fontSize: 12, color: tunnelColor, fontWeight: 700 }}>{tunnelLabel}</span>
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: T.textSecondary }}>
            <span>Endpoint: <code style={{ color: T.accent }}>bridge.yourdomain.com</code></span>
          </div>
          {data.tunnel.latencyMs !== undefined && (
            <div style={{ marginTop: 8, fontSize: 12, color: T.textSecondary }}>
              Latência: <span style={{ color: data.tunnel.latencyMs < 300 ? T.success : data.tunnel.latencyMs < 800 ? T.warning : T.danger, fontWeight: 600 }}>{data.tunnel.latencyMs}ms</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Server Info ── */}
      {srv && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: T.text }}>Informações do Servidor</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 13 }}>
            {[
              { label: 'IP', value: String(srv.ip_address || '—') },
              { label: 'Hostname', value: String(srv.hostname || '—') },
              { label: 'Provider', value: String(srv.provider || '—') },
              { label: 'SSH', value: `root@${srv.ip_address}:${srv.ssh_port || 22}` },
              { label: 'Status', value: String(srv.status || '—') },
              { label: 'OS', value: String(srv.os_info || 'Ubuntu 24.04') },
              { label: 'CPU', value: `${srv.cpu_cores || '—'} cores` },
              { label: 'RAM', value: `${srv.ram_mb || '—'} MB` },
              { label: 'Disco', value: `${srv.disk_gb || '—'} GB` },
              { label: 'Docker', value: String(srv.docker_version || 'Pendente') },
              { label: 'Compose', value: String(srv.docker_compose_version || 'Pendente') },
              { label: 'Max Containers', value: String(srv.max_containers || '—') },
              { label: 'Containers Ativos', value: String(srv.active_containers || 0) },
              { label: 'Vault Credential', value: srv.vault_credential_id ? 'Sim' : 'Não' },
              { label: 'Criado em', value: srv.created_at ? new Date(String(srv.created_at)).toLocaleDateString('pt-BR') : '—' },
            ].map(item => (
              <div key={item.label} style={{ padding: '8px 0', borderBottom: `1px solid ${T.bgHover}55` }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{item.label}</div>
                <div style={{ color: T.text, fontWeight: 600, fontFamily: item.label === 'SSH' || item.label === 'IP' ? 'monospace' : 'inherit' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Jobs KPIs ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Jobs', value: data.jobs.total, color: T.text },
          { label: 'Rodando', value: data.jobs.running, color: T.primary },
          { label: 'Pendentes', value: data.jobs.pending, color: T.warning },
          { label: 'Completos (24h)', value: data.jobs.completed_24h, color: T.success },
          { label: 'Falhas (24h)', value: data.jobs.failed_24h, color: T.danger },
        ].map(k => (
          <div key={k.label} style={kpiStyle(k.color)}>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Recent Jobs ── */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: T.text }}>Jobs Recentes</h3>
        {data.jobs.recent.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Nenhum job registrado ainda</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.jobs.recent.map((j, i) => {
              const jStatus = String(j.status || 'unknown');
              const sc = jStatus === 'completed' ? T.success : jStatus === 'running' ? T.primary : jStatus === 'pending' ? T.warning : T.danger;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: T.bg, borderRadius: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.text, fontWeight: 600, minWidth: 80 }}>{String(j.job_type || '—')}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, flex: 1 }}>{String(j.progress || j.error || '—')}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'monospace' }}>
                    {j.created_at ? new Date(String(j.created_at)).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                  <span style={{ fontSize: 10, color: sc, background: `${sc}15`, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{jStatus}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Events Timeline ── */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: T.text }}>Eventos Recentes</h3>
        {data.events.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Nenhum evento registrado</div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: 20 }}>
            {/* Timeline line */}
            <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: T.borderHover }} />
            {data.events.map((ev, i) => {
              let evData: Record<string, unknown> = {};
              try { evData = JSON.parse(String(ev.event_data || '{}')); } catch { /* */ }
              return (
                <div key={i} style={{ position: 'relative', marginBottom: 12, paddingLeft: 16 }}>
                  {/* Timeline dot */}
                  <div style={{ position: 'absolute', left: -17, top: 4, width: 10, height: 10, borderRadius: '50%', background: T.accent, border: `2px solid ${T.bg}` }} />
                  <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{String(ev.event_type || '—')}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>
                    {ev.created_at ? new Date(String(ev.created_at)).toLocaleString('pt-BR') : '—'}
                  </div>
                  {Object.keys(evData).length > 0 && (
                    <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>
                      {Object.entries(evData).map(([k, v]) => <span key={k} style={{ marginRight: 10 }}>{k}: <strong>{String(v)}</strong></span>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CONTAINERS TAB
// ══════════════════════════════════════════════════════════════════════════

function ContainersTab() {
  const [containers, setContainers] = useState<CloudContainer[]>([]);
  const [stats, setStats] = useState<ContainerStats | null>(null);
  const [servers, setServers] = useState<Array<{ server_id: string; server_name: string; ip_address: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [serverFilter, setServerFilter] = useState('');
  const [search, setSearch] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (serverFilter) params.set('server_id', serverFilter);
      if (search) params.set('search', search);
      const [cData, sData] = await Promise.all([
        api.get<{ containers: CloudContainer[] }>(`/cloud/admin/servers/containers?${params.toString()}`),
        api.get<ContainerStats>('/cloud/admin/servers/containers/stats'),
      ]);
      setContainers(cData.containers);
      setStats(sData);
      setServers(sData.byServer ?? []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [statusFilter, serverFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (val: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearch(val), 300);
  };

  const parseSafe = (json: string | null | undefined): Record<string, unknown> | null => {
    if (!json) return null;
    try { return JSON.parse(json); } catch { return null; }
  };

  const kpiCard = (label: string, value: number, color: string) => (
    <div style={{ flex: 1, minWidth: 120, background: `${T.border}`, borderRadius: 10, border: `1px solid ${T.bgHover}`, padding: '14px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', color: T.text, fontSize: 22, fontWeight: 800 }}>Containers</h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: T.textMuted }}>Todos os containers em todos os servidores</p>

      {/* KPI Cards */}
      {stats && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {kpiCard('Total', stats.totals.total, T.text)}
          {kpiCard('Rodando', stats.totals.running, T.success)}
          {kpiCard('Parados', stats.totals.stopped, T.textMuted)}
          {kpiCard('Erro', stats.totals.error, T.danger)}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Buscar container..." onInput={(e) => handleSearch((e.target as HTMLInputElement).value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 14px', borderRadius: 8, border: '1px solid #374151', background: T.bg, color: T.text, fontSize: 13, outline: 'none' }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: T.bg, color: T.text, fontSize: 13 }}>
          <option value="">Todos os status</option>
          <option value="running">Rodando</option>
          <option value="created">Criado</option>
          <option value="provisioning">Provisionando</option>
          <option value="stopped">Parado</option>
          <option value="error">Erro</option>
        </select>
        {servers.length > 0 && (
          <select value={serverFilter} onChange={(e) => setServerFilter((e.target as HTMLSelectElement).value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #374151', background: T.bg, color: T.text, fontSize: 13 }}>
            <option value="">Todos os servidores</option>
            {servers.map(s => <option key={s.server_id} value={s.server_id}>{s.server_name || s.ip_address}</option>)}
          </select>
        )}
        <span style={{ fontSize: 12, color: T.textMuted, alignSelf: 'center' }}>{loading ? '...' : `${containers.length} containers`}</span>
      </div>

      {loading && containers.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}><ListSkeleton rows={3} /></div>
      ) : containers.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, background: `${T.border}`, borderRadius: 12 }}>
          Nenhum container criado. Containers sao criados automaticamente pelo Pipeline de Workspace.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {containers.map(ct => {
            const ports = parseSafe(ct.ports);
            const resources = parseSafe(ct.resources);
            return (
              <div key={ct.id} style={{ background: `${T.border}`, borderRadius: 10, border: `1px solid ${T.bgHover}`, padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: containerStatusColor(ct.status), flexShrink: 0 }} />
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{ct.name}</span>
                      <span style={{ fontSize: 11, color: T.textMuted, background: T.bg, padding: '2px 8px', borderRadius: 4 }}>{containerStatusLabel(ct.status)}</span>
                      {ct.plan_tier && (
                        <span style={{ fontSize: 11, color: T.accent, background: '#7c5cfc15', padding: '2px 8px', borderRadius: 4 }}>{ct.plan_tier}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: T.textMuted, flexWrap: 'wrap' }}>
                      {ct.server_name && <span>SRV: {ct.server_name}</span>}
                      {ct.server_ip && <span style={{ fontFamily: 'monospace' }}>{ct.server_ip}</span>}
                      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{ct.image}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: T.textSecondary, flexWrap: 'wrap' }}>
                      {ports && Object.entries(ports).map(([k, v]) => (
                        <span key={k} style={{ fontFamily: 'monospace' }}>{k}:{String(v)}</span>
                      ))}
                      {resources && (
                        <>
                          {(resources as Record<string, string>).cpu && <span>CPU: {(resources as Record<string, string>).cpu}</span>}
                          {(resources as Record<string, string>).memory && <span>RAM: {(resources as Record<string, string>).memory}</span>}
                          {(resources as Record<string, string>).disk && <span>Disco: {(resources as Record<string, string>).disk}</span>}
                        </>
                      )}
                    </div>
                    {(ct.customer_id || ct.domain) && (
                      <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 11, color: T.textSecondary }}>
                        {ct.customer_id && <span>Cliente: {ct.customer_id.slice(0, 8)}...</span>}
                        {ct.domain && <span>{ct.domain}</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: T.textMuted }}>
                      {ct.container_docker_id ? <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{ct.container_docker_id.slice(0, 12)}</span> : '--'}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                      {new Date(ct.created_at).toLocaleDateString('pt-BR')} {new Date(ct.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// IMAGES TAB
// ══════════════════════════════════════════════════════════════════════════

function ImagesTab() {
  const [images, setImages] = useState<DockerImage[]>([]);
  const [defaultImage, setDefaultImage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<{ images: DockerImage[]; defaultImage: string }>('/cloud/admin/servers/docker/images');
        setImages(data.images ?? []);
        setDefaultImage(data.defaultImage ?? '');
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><ListSkeleton rows={3} /></div>;

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', color: T.text, fontSize: 22, fontWeight: 800 }}>Imagens</h2>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: T.textMuted }}>Imagens Docker disponíveis para criação de workspaces</p>

      {/* Default image card */}
      <div style={{ background: `${T.border}`, borderRadius: 12, border: '1px solid #7c5cfc33', padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: T.accent, background: '#7c5cfc15', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>PADRÃO</span>
          <span style={{ fontSize: 15, color: T.text, fontWeight: 700 }}>Imagem Docker Principal</span>
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 14, color: T.success, background: T.bg, padding: '10px 14px', borderRadius: 8 }}>
          {defaultImage || 'Não configurada'}
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: T.textMuted }}>
          Configurada em Configurações → Padrões de Container. Usada como base para novos workspaces.
        </p>
      </div>

      {/* Images list */}
      {images.length > 0 ? (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.text }}>Imagens Registradas</h3>
          {images.map((img: DockerImage) => (
            <div key={img.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.bgHover}` }}>
              <div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{img.name}:{img.tag}</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>{img.registry} {img.size_mb ? `• ${img.size_mb} MB` : ''}</div>
              </div>
              <span style={{ fontSize: 11, color: T.textMuted }}>{img.created_at ? new Date(img.created_at).toLocaleDateString('pt-BR') : ''}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}></div>
          <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 4 }}>Nenhuma imagem registrada ainda</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Imagens serão registradas automaticamente quando containers forem criados</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// STATS TAB
// ══════════════════════════════════════════════════════════════════════════

function StatsTab() {
  const [stats, setStats] = useState<DockerStatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<DockerStatsData>('/cloud/admin/servers/docker/stats');
        setStats(data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><ListSkeleton rows={3} /></div>;

  const t = stats?.totals ?? { total: 0, running: 0, provisioning: 0, stopped_or_error: 0 };
  const runPct = t.total > 0 ? Math.round((t.running / t.total) * 100) : 0;

  const kpiCard = (label: string, value: string | number, color: string, icon: string) => (
    <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: '20px 24px', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>{icon} {label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
    </div>
  );

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', color: T.text, fontSize: 22, fontWeight: 800 }}>Estatísticas</h2>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: T.textMuted }}>Visão geral do ambiente Docker</p>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {kpiCard('Total Containers', t.total, T.text, '')}
        {kpiCard('Rodando', t.running, T.success, '')}
        {kpiCard('Provisionando', t.provisioning, T.warning, '')}
        {kpiCard('Parados / Erro', t.stopped_or_error, T.danger, '')}
      </div>

      {/* Health bar */}
      <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: T.textSecondary, fontWeight: 600 }}>Taxa de Disponibilidade</span>
          <span style={{ fontSize: 13, color: T.success, fontWeight: 700 }}>{runPct}%</span>
        </div>
        <div style={{ height: 10, background: T.borderHover, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${runPct}%`, background: `linear-gradient(90deg, ${T.success}, #34D399)`, borderRadius: 6, transition: 'width 0.5s' }} />
        </div>
      </div>

      {/* By Server distribution */}
      {(stats?.byServer?.length ?? 0) > 0 && (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.text }}>Distribuição por Servidor</h3>
          {stats!.byServer.map(s => {
            const pct = s.max_containers > 0 ? Math.round((s.container_count / s.max_containers) * 100) : 0;
            return (
              <div key={s.server_id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{s.server_name || s.ip_address}</span>
                  <span style={{ fontSize: 12, color: T.textSecondary }}>
                    {s.running_count}/{s.container_count} rodando • {s.container_count}/{s.max_containers} slots ({pct}%)
                  </span>
                </div>
                <div style={{ height: 8, background: T.borderHover, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? T.danger : pct > 70 ? T.warning : T.accent, borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* By Status breakdown */}
      {(stats?.byStatus?.length ?? 0) > 0 && (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.text }}>Por Status</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {stats!.byStatus.map(s => (
              <div key={s.status} style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.bg, borderRadius: 8, padding: '8px 14px' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: containerStatusColor(s.status) }} />
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{containerStatusLabel(s.status)}</span>
                <span style={{ fontSize: 13, color: T.textSecondary }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Workspace Type */}
      {(stats?.byType?.length ?? 0) > 0 && (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.text }}>Por Tipo de Workspace</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {stats!.byType.map(wt => (
              <div key={wt.workspace_type} style={{ background: T.bg, borderRadius: 8, padding: '8px 14px' }}>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{wt.workspace_type || 'vps'}</span>
                <span style={{ fontSize: 13, color: T.textSecondary, marginLeft: 8 }}>{wt.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent containers */}
      {(stats?.recentContainers?.length ?? 0) > 0 && (
        <div style={{ background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: T.text }}>Últimos Containers Criados</h3>
          {stats!.recentContainers.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.bgHover}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: containerStatusColor(c.status) }} />
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{c.display_name || c.name}</span>
              </div>
              <span style={{ fontSize: 12, color: T.textMuted }}>{new Date(c.created_at).toLocaleDateString('pt-BR')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ══════════════════════════════════════════════════════════════════════════

interface CloudSettings {
  provisioningServer: {
    ip: string;
    sshPort: number;
    healthPort: number;
    label: string;
    notes: string;
    serverId: string;
  };
  defaults: {
    dockerImage: string;
    portRangeStart: number;
    portRangeEnd: number;
    maxContainersPerServer: number;
    containerMemoryMb: number;
    containerCpuShares: number;
  };
  notifications: {
    alertOnProvisioningFailure: boolean;
    alertOnServerUnhealthy: boolean;
    alertOnCapacity90: boolean;
  };
}

const DEFAULT_SETTINGS: CloudSettings = {
  provisioningServer: { ip: '', sshPort: 22, healthPort: 18790, label: '', notes: 'LEGACY V1 REMOVED (FASE 5) — Migrado para DOKS', serverId: '' }, // was 195.35.18.138
  defaults: { dockerImage: 'ghcr.io/org/workspace:latest', portRangeStart: 10000, portRangeEnd: 39999, maxContainersPerServer: 20, containerMemoryMb: 512, containerCpuShares: 256 },
  notifications: { alertOnProvisioningFailure: true, alertOnServerUnhealthy: true, alertOnCapacity90: true },
};

const KNOWN_DOCKER_IMAGES_FALLBACK = [
  { value: 'ghcr.io/org/workspace:latest', label: 'WhiteLabelClaw Workspace (latest)' },
  { value: 'ghcr.io/org/workspace:stable', label: 'WhiteLabelClaw Workspace (stable)' },
  { value: 'ubuntu:24.04', label: 'Ubuntu 24.04' },
];

function SettingsTab() {
  const [settings, setSettings] = useState<CloudSettings>(JSON.parse(JSON.stringify(DEFAULT_SETTINGS)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [servers, setServers] = useState<Array<{ id: string; name: string; ip_address: string }>>([]);
  const [customImage, setCustomImage] = useState(false);
  const [dbImages, setDbImages] = useState<Array<{ value: string; label: string }>>([]);

  // Merged image list: DB images first, then fallback, deduplicated
  const KNOWN_DOCKER_IMAGES = useMemo(() => {
    const merged = [...dbImages];
    for (const fb of KNOWN_DOCKER_IMAGES_FALLBACK) {
      if (!merged.find(i => i.value === fb.value)) merged.push(fb);
    }
    return merged;
  }, [dbImages]);

  useEffect(() => {
    (async () => {
      try {
        const [settingsData, serversData, imagesData] = await Promise.all([
          api.get<{ settings: CloudSettings | null }>('/cloud/admin/servers/settings'),
          api.get<{ servers: Array<{ id: string; name: string; ip_address: string }> }>('/cloud/admin/servers?pageSize=100'),
          api.get<{ images: Array<{ name: string; registry_url: string; tag: string; is_default: number; is_active: number }> }>('/cloud/admin/servers/docker/images'),
        ]);
        if (settingsData.settings) {
          setSettings(prev => ({ ...prev, ...settingsData.settings, provisioningServer: { ...prev.provisioningServer, ...settingsData.settings!.provisioningServer }, defaults: { ...prev.defaults, ...settingsData.settings!.defaults }, notifications: { ...prev.notifications, ...settingsData.settings!.notifications } }));
        }
        setServers(serversData.servers ?? []);
        // Build image list from DB
        const dbImgs = (imagesData.images ?? []).filter(i => i.is_active).map(i => ({
          value: `${i.registry_url}:${i.tag}`,
          label: `${i.name} (${i.tag})${i.is_default ? ' - Padrão' : ''}`,
        }));
        setDbImages(dbImgs);
        // Check if current image is custom
        const img = settingsData.settings?.defaults?.dockerImage || DEFAULT_SETTINGS.defaults.dockerImage;
        const allValues = [...dbImgs.map(i => i.value), ...KNOWN_DOCKER_IMAGES_FALLBACK.map(i => i.value)];
        if (!allValues.includes(img)) setCustomImage(true);
      } catch { /* use defaults */ }
      setLoading(false);
    })();
  }, []);

  const update = <K extends keyof CloudSettings>(section: K, field: string, value: unknown) => {
    setSettings(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/cloud/admin/servers/settings', settings);
      showToast('Configurações salvas ', 'success');
      setDirty(false);
      setEditMode(false);
    } catch { showToast('Erro ao salvar', 'error'); }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><ListSkeleton rows={3} /></div>;
  }

  const inputStyle: JSX.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #374151',
    background: editMode ? T.bg : '#0d1117aa', color: editMode ? T.text : T.textSecondary,
    fontSize: 14, outline: 'none', boxSizing: 'border-box', cursor: editMode ? 'text' : 'default',
  };
  const selectStyle: JSX.CSSProperties = {
    ...inputStyle, cursor: editMode ? 'pointer' : 'default', appearance: 'auto' as JSX.CSSProperties['appearance'],
  };
  const labelStyle: JSX.CSSProperties = { display: 'block', fontSize: 12, color: T.textSecondary, marginBottom: 6, fontWeight: 600 };
  const sectionStyle: JSX.CSSProperties = { background: `${T.border}`, borderRadius: 12, border: `1px solid ${T.bgHover}`, padding: 24, marginBottom: 20 };
  const sectionTitle: JSX.CSSProperties = { margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 8 };

  const isConfigured = !!settings.provisioningServer.ip;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: '0 0 6px', color: T.text, fontSize: 22, fontWeight: 800 }}>Configurações</h2>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Configurações gerais do sistema Elon Cloud</p>
        </div>
        {!editMode ? (
          <Button variant="ghost" onClick={() => setEditMode(true)}>Editar</Button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={() => { setEditMode(false); setDirty(false); }}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        )}
      </div>

      {/* ── Provisioning Server (compact — full dashboard in Provisionamento tab) ── */}
      <div style={{ ...sectionStyle, border: isConfigured ? `1px solid ${T.success}33` : `1px solid ${T.warning}33` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ ...sectionTitle, margin: 0 }}>Servidor de Provisionamento</h3>
            {isConfigured ? (
              <span style={{ fontSize: 11, color: T.success, background: `${T.success}15`, padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>{settings.provisioningServer.ip}</span>
            ) : (
              <span style={{ fontSize: 11, color: T.warning, background: `${T.warning}15`, padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>Não configurado</span>
            )}
          </div>
          <span style={{ fontSize: 12, color: T.textMuted }}>Veja detalhes na aba <strong style={{ color: T.accent }}>Provisionamento</strong></span>
        </div>

        {editMode && (
          <div style={{ marginTop: 16 }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: T.textMuted }}>Configurar qual servidor atua como provisionador:</p>
            {servers.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Servidor</label>
                <select value={settings.provisioningServer.serverId} onChange={(e) => {
                  const sid = (e.target as HTMLSelectElement).value;
                  const srv = servers.find(s => s.id === sid);
                  if (srv) { update('provisioningServer', 'serverId', sid); update('provisioningServer', 'ip', srv.ip_address); update('provisioningServer', 'label', srv.name); }
                  else { update('provisioningServer', 'serverId', ''); }
                }} style={selectStyle}>
                  <option value="">— Selecionar —</option>
                  {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.ip_address})</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>IP</label>
                <input style={inputStyle} value={settings.provisioningServer.ip} onInput={(e) => update('provisioningServer', 'ip', (e.target as HTMLInputElement).value)} />
              </div>
              <div>
                <label style={labelStyle}>Porta SSH</label>
                <input style={inputStyle} type="number" value={settings.provisioningServer.sshPort} onInput={(e) => update('provisioningServer', 'sshPort', parseInt((e.target as HTMLInputElement).value) || 22)} />
              </div>
              <div>
                <label style={labelStyle}>Porta Health</label>
                <input style={inputStyle} type="number" value={settings.provisioningServer.healthPort} onInput={(e) => update('provisioningServer', 'healthPort', parseInt((e.target as HTMLInputElement).value) || 18790)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Default Values ── */}
      <div style={sectionStyle}>
        <h3 style={sectionTitle}>Padrões de Container</h3>
        <p style={{ margin: '-8px 0 16px', fontSize: 12, color: T.textMuted }}>
          Valores padrão para criação de novos workspaces
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Imagem Docker Padrão</label>
          {!customImage ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <select disabled={!editMode} value={settings.defaults.dockerImage} onChange={(e) => {
                const val = (e.target as HTMLSelectElement).value;
                if (val === '__custom__') { setCustomImage(true); } else { update('defaults', 'dockerImage', val); }
              }} style={{ ...selectStyle, flex: 1 }}>
                {KNOWN_DOCKER_IMAGES.map(img => <option key={img.value} value={img.value}>{img.label}</option>)}
                <option value="__custom__">Digitar manualmente...</option>
              </select>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input disabled={!editMode} style={{ ...inputStyle, fontFamily: 'monospace', flex: 1 }} value={settings.defaults.dockerImage}
                placeholder="registry/image:tag"
                onInput={(e) => update('defaults', 'dockerImage', (e.target as HTMLInputElement).value)} />
              {editMode && (
                <Button variant="ghost" onClick={() => { setCustomImage(false); update('defaults', 'dockerImage', KNOWN_DOCKER_IMAGES[0].value); }}>Lista</Button>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, fontFamily: 'monospace' }}>
            Atual: {settings.defaults.dockerImage}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Porta Inicial</label>
            <input disabled={!editMode} style={inputStyle} type="number" value={settings.defaults.portRangeStart}
              onInput={(e) => update('defaults', 'portRangeStart', parseInt((e.target as HTMLInputElement).value) || 10000)} />
          </div>
          <div>
            <label style={labelStyle}>Porta Final</label>
            <input disabled={!editMode} style={inputStyle} type="number" value={settings.defaults.portRangeEnd}
              onInput={(e) => update('defaults', 'portRangeEnd', parseInt((e.target as HTMLInputElement).value) || 39999)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Max Containers / Servidor</label>
            <input disabled={!editMode} style={inputStyle} type="number" value={settings.defaults.maxContainersPerServer}
              onInput={(e) => update('defaults', 'maxContainersPerServer', parseInt((e.target as HTMLInputElement).value) || 20)} />
          </div>
          <div>
            <label style={labelStyle}>Memória (MB)</label>
            <input disabled={!editMode} style={inputStyle} type="number" value={settings.defaults.containerMemoryMb}
              onInput={(e) => update('defaults', 'containerMemoryMb', parseInt((e.target as HTMLInputElement).value) || 512)} />
          </div>
          <div>
            <label style={labelStyle}>CPU Shares</label>
            <input disabled={!editMode} style={inputStyle} type="number" value={settings.defaults.containerCpuShares}
              onInput={(e) => update('defaults', 'containerCpuShares', parseInt((e.target as HTMLInputElement).value) || 256)} />
          </div>
        </div>
      </div>

      {/* ── Notifications ── */}
      <div style={sectionStyle}>
        <h3 style={sectionTitle}>Notificações</h3>
        <p style={{ margin: '-8px 0 16px', fontSize: 12, color: T.textMuted }}>
          Alertas automáticos para eventos críticos
        </p>

        {([
          { key: 'alertOnProvisioningFailure', label: 'Falha no provisionamento', desc: 'Notificar quando um pipeline falhar' },
          { key: 'alertOnServerUnhealthy', label: 'Servidor degradado', desc: 'Notificar quando health check falhar' },
          { key: 'alertOnCapacity90', label: 'Capacidade > 90%', desc: 'Notificar quando servidor atingir 90% de containers' },
        ] as const).map(n => (
          <div key={n.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.bgHover}` }}>
            <div>
              <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{n.label}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{n.desc}</div>
            </div>
            <Button variant="ghost" onClick={() => update('notifications', n.key, !settings.notifications[n.key])} disabled={!editMode}>
              <span style={{
                position: 'absolute', top: 2, left: settings.notifications[n.key] ? 22 : 2,
                width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
              }} />
            </Button>
          </div>
        ))}
      </div>

      {/* ── Sticky Save Bar ── */}
      {dirty && editMode && (
        <div style={{
          position: 'sticky', bottom: 16, background: T.borderHover, borderRadius: 12, padding: '12px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          border: '1px solid #7c5cfc33', boxShadow: `0 -4px 20px ${T.bgOverlay}`,
        }}>
          <span style={{ color: T.warning, fontSize: 13, fontWeight: 600 }}>Alterações não salvas</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={() => { setEditMode(false); setDirty(false); }}>Descartar</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════

type MainTab = 'servers' | 'containers' | 'images' | 'stats' | 'monitoring' | 'settings'; // LEGACY V1 REMOVED (FASE 5): removed 'provisioning' | 'pipeline'

interface PageProps {
  path?: string;
}

export function AdminCloudServers(_props: PageProps) {
  const [mainTab, setMainTab] = useState<MainTab>('servers');
  const [servers, setServers] = useState<CloudServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'load' | 'created'>('name');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [pendingJobsCount, setPendingJobsCount] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(pagination.page));
      params.set('pageSize', '50');

      const [data, jobsData] = await Promise.all([
        api.get<{ servers: CloudServer[]; pagination: Pagination }>(`/cloud/admin/servers?${params.toString()}`),
        api.get<{ jobs: ProvisioningJob[] }>('/cloud/admin/servers/pending-jobs').catch(() => ({ jobs: [] as ProvisioningJob[] })),
      ]);

      let sorted = [...data.servers];
      if (sortBy === 'load') {
        sorted.sort((a, b) => loadPercent(b) - loadPercent(a));
      } else if (sortBy === 'created') {
        sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }
      setServers(sorted);
      setPagination(data.pagination ?? { page: 1, pageSize: 50, total: data.servers?.length ?? 0, totalPages: 1 });
      setPendingJobsCount(jobsData.jobs.length);
    } catch {
      setError('Erro ao carregar servidores. Verifique sua conexão.');
      showToast('Erro ao carregar servidores', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, pagination.page, sortBy]);

  useEffect(() => { fetchServers(); }, [fetchServers]);

  const handleSearch = (val: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearch(val), 300);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/cloud/admin/servers/${id}`);
      showToast('Servidor excluído com sucesso', 'success');
      setDeleteConfirm(null);
      fetchServers();
    } catch {
      showToast('Erro ao excluir servidor', 'error');
    }
  };

  const handleServerAction = async (id: string, action: string, label: string, e: Event) => {
    e.stopPropagation();
    try {
      await api.post(`/cloud/admin/servers/${id}/${action}`, {});
      showToast(`${label} executado`, 'success');
      fetchServers();
    } catch {
      showToast(`Erro: ${label}`, 'error');
    }
  };

  // Detail view
  if (selectedId) {
    return <ServerDetailView serverId={selectedId} onBack={() => { setSelectedId(null); fetchServers(); }} />;
  }

  // Compute KPIs
  const totalServers = pagination.total;
  const readyCount = servers.filter(s => s.status === 'ready').length;
  const totalActiveContainers = servers.reduce((sum, s) => sum + s.active_containers, 0);
  const totalMaxContainers = servers.reduce((sum, s) => sum + s.max_containers, 0);
  const capacityPct = totalMaxContainers > 0 ? Math.round((totalActiveContainers / totalMaxContainers) * 100) : 0;

  return (
    <div style={{ padding: '0 4px' }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.5; }
          50% { opacity: 1; }
          100% { opacity: 0.5; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .server-card:hover {
          border-color: #7c5cfc33 !important;
          transform: translateY(-1px);
        }
      `}</style>

      {/* Main Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: T.bg, borderRadius: 12, padding: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', maxWidth: '100%' }}>
        {([
          { key: 'servers' as MainTab, label: 'Servidores' },
          // LEGACY V1 REMOVED (FASE 5): { key: 'provisioning' as MainTab, label: 'Provisionar' },
          { key: 'containers' as MainTab, label: 'Containers' },
          { key: 'images' as MainTab, label: 'Imagens' },
          { key: 'stats' as MainTab, label: 'Stats' },
          // LEGACY V1 REMOVED (FASE 5): { key: 'pipeline' as MainTab, label: 'Pipeline' },
          { key: 'monitoring' as MainTab, label: 'Monitor' },
          { key: 'settings' as MainTab, label: 'Config' },
        ]).map(t => (
          <Button variant="ghost" onClick={() => setMainTab(t.key)}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* LEGACY V1 REMOVED (FASE 5): Provisioning Tab */}
      {/* {mainTab === 'provisioning' && <ProvisioningTab />} */}

      {/* Containers Tab */}
      {mainTab === 'containers' && <ContainersTab />}

      {/* Images Tab */}
      {mainTab === 'images' && <ImagesTab />}

      {/* Stats Tab */}
      {mainTab === 'stats' && <StatsTab />}

      {/* LEGACY V1 REMOVED (FASE 5): Pipeline Tab */}
      {/* {mainTab === 'pipeline' && <PipelineTab />} */}

      {/* Monitoring Tab */}
      {mainTab === 'monitoring' && <MonitoringTab />}

      {/* Settings Tab */}
      {mainTab === 'settings' && <SettingsTab />}

      {/* Servers Tab */}
      {mainTab === 'servers' && <>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: T.text, fontSize: 22, fontWeight: 800 }}>Servidores</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: T.textMuted }}>Gerencie seus servidores de hospedagem de containers</p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          + Adicionar Servidor
        </Button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KPICard icon="" label="Total Servidores" value={totalServers} color={T.primary} />
        <KPICard icon="" label="Prontos" value={readyCount} sub={`de ${totalServers} servidores`} color={T.success} />
        <KPICard icon="" label="Containers Ativos" value={totalActiveContainers} sub={`de ${totalMaxContainers} slots`} color={T.purple} />
        <KPICard icon="" label="Capacidade" value={`${capacityPct}%`}
          sub={<ProgressBar value={totalActiveContainers} max={totalMaxContainers} height={6} /> as unknown as string}
          color={loadColor(capacityPct)} />
        <KPICard icon="" label="Jobs Pendentes" value={pendingJobsCount} color={T.warning} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.textMuted }}></span>
          <input type="text" placeholder="Buscar por nome, hostname ou IP..."
            onInput={(e) => handleSearch((e.target as HTMLInputElement).value)}
            style={{
              width: '100%', padding: '10px 12px 10px 36px', borderRadius: 10,
              border: `1px solid ${T.bgHover}`, background: T.bg, color: T.text,
              fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}
          style={{
            padding: '10px 14px', borderRadius: 10, border: `1px solid ${T.bgHover}`,
            background: T.bg, color: T.text, fontSize: 13, cursor: 'pointer',
          }}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy((e.target as HTMLSelectElement).value as typeof sortBy)}
          style={{
            padding: '10px 14px', borderRadius: 10, border: `1px solid ${T.bgHover}`,
            background: T.bg, color: T.text, fontSize: 13, cursor: 'pointer',
          }}>
          <option value="name">Ordenar: Nome</option>
          <option value="load">Ordenar: Carga</option>
          <option value="created">Ordenar: Recente</option>
        </select>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div style={{
          padding: 20, background: `${T.danger}15`, border: `${T.danger}15`, borderRadius: 12,
          textAlign: 'center', marginBottom: 16,
        }}>
          <div style={{ fontSize: 14, color: T.danger, fontWeight: 600, marginBottom: 8 }}>{error}</div>
          <Button variant="ghost" onClick={fetchServers}>
            Tentar Novamente
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && servers.length === 0 && (
        <EmptyState icon="" title="Nenhum servidor encontrado"
          desc={search || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Adicione seu primeiro servidor para começar a hospedar containers.'}
          action={!search && !statusFilter ? '+ Adicionar Servidor' : undefined}
          onAction={!search && !statusFilter ? () => setShowAdd(true) : undefined} />
      )}

      {/* Server Cards Grid */}
      {!loading && !error && servers.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
          {servers.map(server => {
            const lPct = loadPercent(server);
            const tiers = parsePlanTiers(server.plan_tiers);
            return (
              <div key={server.id} className="server-card" onClick={() => setSelectedId(server.id)}
                style={{
                  background: `${T.border}`, border: `1px solid ${T.bgHover}`, borderRadius: 12,
                  padding: 20, cursor: 'pointer', transition: 'all 0.2s ease',
                  position: 'relative',
                }}>
                {/* Header Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: T.text, fontSize: 15, marginBottom: 2 }}>{server.name}</div>
                    {server.hostname && <div style={{ fontSize: 11, color: T.textMuted }}>{server.hostname}</div>}
                  </div>
                  <StatusLED status={server.status} />
                </div>

                {/* IP + Provider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span onClick={(e) => { e.stopPropagation(); copyToClipboard(server.ip_address); }}
                    style={{
                      fontFamily: 'monospace', fontSize: 12, color: T.textSecondary,
                      padding: '2px 6px', background: T.bg, borderRadius: 4,
                      cursor: 'pointer', border: `1px solid ${T.bgHover}`,
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}>
                    {server.ip_address}
                  </span>
                  {server.provider && <span style={{ fontSize: 11, color: T.textMuted }}>{server.provider}</span>}
                  {server.region && <span style={{ fontSize: 11, color: T.textMuted }}>{server.region}</span>}
                </div>

                {/* Specs Row */}
                {(server.cpu_cores || server.ram_mb || server.disk_gb) && (
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: T.textSecondary, marginBottom: 10 }}>
                    {server.cpu_cores && <span>{server.cpu_cores} cores</span>}
                    {server.ram_mb && <span>{(server.ram_mb / 1024).toFixed(0)} GB</span>}
                    {server.disk_gb && <span>{server.disk_gb} GB</span>}
                    {server.os_info && <span>{server.os_info.slice(0, 20)}</span>}
                  </div>
                )}

                {/* Container Load */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textSecondary, marginBottom: 3 }}>
                    <span>{server.active_containers}/{server.max_containers} containers</span>
                    <span style={{ fontWeight: 600, color: loadColor(lPct) }}>{lPct}%</span>
                  </div>
                  <ProgressBar value={server.active_containers} max={server.max_containers} />
                </div>

                {/* Plan Tiers */}
                {tiers.length > 0 && (
                  <div style={{ display: 'flex', gap: 3, marginBottom: 10, flexWrap: 'wrap' }}>
                    {tiers.map(t => <PlanBadge key={t} plan={t} />)}
                  </div>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: HEALTH_COLORS[server.health_status] ?? T.textMuted,
                    }} />
                    {server.health_status} · {timeAgo(server.last_health_check)}
                  </div>

                  {/* Actions dropdown (simple inline) */}
                  <div style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => handleServerAction(server.id, 'health-check', 'Health Check', e as unknown as Event)} title="Health Check"></Button>
                    <Button variant="ghost" onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => handleServerAction(server.id, 'validate', 'Validar', e as unknown as Event)} title="Validar"></Button>
                    <Button variant="ghost" onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => { e.stopPropagation(); setDeleteConfirm(server.id); }} title="Excluir"></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page <= 1}>
            ← Anterior
          </Button>
          <span style={{ padding: '8px 12px', fontSize: 13, color: T.textSecondary }}>
            Página {pagination.page} de {pagination.totalPages}
          </span>
          <Button variant="ghost" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.totalPages}>
            Próxima →
          </Button>
        </div>
      )}

      </>}

      {/* Add Modal */}
      {showAdd && <AddServerModalEnhanced onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); fetchServers(); }} />}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Excluir Servidor"
          message="Tem certeza que deseja excluir este servidor? Esta ação não pode ser desfeita. Servidores com containers ativos não podem ser excluídos."
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

const miniActionBtn: Record<string, string | number> = {
  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4,
  borderRadius: 4, opacity: 0.6, transition: 'opacity 0.2s',
};
